import { useState, useEffect, useContext, useRef, createContext } from 'react';
import { createPortal } from 'react-dom';
import api from '../../hooks/useApi';
import { AppContext, useApp } from '../../context';
import { useDarkMode } from '../../hooks/useDarkMode';
import {
  formatValueGbp, valueBadges,
  goneQuiet, deadlineSort, fmtDeadlineWithTime, valueTotals,
} from '../../utils/bidValue';
import {
  buildReportSheets, fmtMoney, fmtPercent, fmtDateLong,
  previousCalendarMonth, thisMonthRange, lastMonthRange,
} from '../../utils/bidReportExport';
import { downloadBidReport } from './bidReportSheet';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { BID_TRACKER_TOUR_ID, bidTrackerTourSteps } from './bidTrackerTour';
import {
  bidUrgency as urgencyOf, pipelineKpis, boardColumns, weekAgo, statusChangesThisWeek, checkedWithNoChange,
  ROW_FIELDS, initialFields, fieldValue, moveField, maskMoney, PRE_SUBMISSION,
  buyerList, contactCards, refereeLoad, actionOwnerId, waitingOn, triageQueue,
  LEAD_STATUSES, LEAD_QUICK, splitByKind, followUpState, leadKpis, sortLeads,
} from '../../utils/bidTracker';

// Alton Valley — Bid Tracker.
// Ported from the standalone crm.altonvalley.com app into the Ops Business Dev
// tab, cut back to three tabs by the redesign of 17 September 2026, then
// rebuilt by the CRM redesign of 29 September 2026 (build step 1) as Pipeline,
// My bids, Bids, Review and Reports. Lists are rows that wrap rather than a
// table that scrolls sideways; the fields shown under each row, the filters
// and settings such as Hide values save against the person's login
// (people/me/views), not the browser.

// ============ Domain constants ============
// The bid workflow. ACTIVE_STAGES is the controllable middle section (bumped
// forward or back via the stepper on the Bid Detail page); Won/Lost/No-bid/
// Parked are outcomes reachable from any active stage, and reversible back
// to whichever stage the bid was in via `previous_status` (same pattern as
// SOW statuses) if recorded in error.
export const ACTIVE_STAGES = ['Evaluating', 'Responding', 'Responded', 'Awaiting decision', 'In Discussion'];
export const OUTCOME_STATUSES = ['Won', 'Lost', 'No-bid', 'Parked'];
const STATUSES = ['Upcoming', ...ACTIVE_STAGES, ...OUTCOME_STATUSES];
export const OPEN_STATUSES = ['Upcoming', ...ACTIVE_STAGES];
const RESULTS = ['Won', 'Lost', 'Deselected', 'Excluded'];

export const CAUSES = [
  { key: 'cause_telling', short: 'Telling not showing', help: 'Described what was done, not how / the impact.' },
  { key: 'cause_evidence', short: 'No evidence / metrics', help: 'Claims without hard numbers or proof.' },
  { key: 'cause_compliance', short: 'Compliance / instruction miss', help: 'Missed a mandatory requirement or instruction.' },
  { key: 'cause_generic', short: 'Generic / untailored', help: 'Boilerplate, not written for this buyer.' },
  { key: 'cause_hedged', short: 'Hedged commitment', help: 'Soft language vs. a competitor’s firm assurance.' },
];

// ============ Saved view (per person, follows them between machines) ============
// Column order, hidden columns, filters and the table/board choice are one
// object saved against the login through people/me/views/bid-tracker. Writes
// are debounced so dragging a column or typing a search does not save on
// every keystroke. Nothing here touches localStorage.
const COL_TYPES = ['text', 'number', 'date', 'select'];
export const VIEW_PAGE = 'bid-tracker';
// The columns a person sees until they choose otherwise. The legacy fields
// (key_date, outcome) and the noisier ones stay available under ⚙ Columns.
export const DEFAULT_HIDDEN = ['submission_types', 'service_line', 'sector', 'key_date', 'outcome', 'created_at', 'created_by'];
export const DEFAULT_VIEW = { order: [], hidden: DEFAULT_HIDDEN, filters: { quick: 'open', status: '', sector: '', text: '' }, mode: 'table' };

export function useSavedView(page = VIEW_PAGE) {
  const [view, setView] = useState(DEFAULT_VIEW);
  const [loaded, setLoaded] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    let alive = true;
    api.get(`people/me/views/${page}`)
      .then(d => { if (alive && d?.view && typeof d.view === 'object') setView(v => ({ ...v, ...d.view, filters: { ...v.filters, ...(d.view.filters || {}) } })); })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [page]);
  const update = (patch) => {
    setView(v => {
      const next = typeof patch === 'function' ? patch(v) : { ...v, ...patch };
      clearTimeout(timer.current);
      timer.current = setTimeout(() => { api.put(`people/me/views/${page}`, { view: next }).catch(() => {}); }, 600);
      return next;
    });
  };
  return [view, update, loaded];
}

const dash = s => (s == null || s === '' ? '—' : s);
// submission_types arrives as a JSON array string from a json_group_array()
// subquery — filter(Boolean) guards against any stray null entries.
export function parseSubmissionTypes(v) {
  try { const a = JSON.parse(v || '[]'); return Array.isArray(a) ? a.filter(Boolean) : []; } catch { return []; }
}
export function fmtCreated(v) {
  if (!v) return '—';
  const d = new Date(v.includes('T') ? v : v.replace(' ', 'T') + 'Z');
  return isNaN(d) ? v : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function parseCustom(b) { try { return b && b.custom_json ? JSON.parse(b.custom_json) : {}; } catch { return {}; } }
function parseOptions(col) { try { return col.options ? JSON.parse(col.options) : []; } catch { return []; } }

// ============ Tag colours ============
export const STATUS_COLORS = {
  'Upcoming':     { bg: '#EEF2FF', color: '#4F46E5' },
  'Evaluating':   { bg: '#FFF7ED', color: '#F97316' },
  'Responding':   { bg: '#FFFBEB', color: '#D97706' },
  'Responded':    { bg: '#FEFCE8', color: '#A16207' },
  'Awaiting decision': { bg: '#E6EDFB', color: '#1D4ED8' },
  'In Discussion':{ bg: '#ECFEFF', color: '#0891B2' },
  'Won':          { bg: '#F0FDF4', color: '#22C55E' },
  'Lost':         { bg: '#FEF2F2', color: '#EF4444' },
  'No-bid':       { bg: '#F3F4F6', color: '#6B7280' },
  'Parked':       { bg: '#F3F4F6', color: '#6B7280' },
};
export const RESULT_COLORS = {
  'Won':        { bg: '#F0FDF4', color: '#22C55E' },
  'Lost':       { bg: '#FEF2F2', color: '#EF4444' },
  'Deselected': { bg: '#FEF3C7', color: '#B45309' },
  'Excluded':   { bg: '#FEF2F2', color: '#EF4444' },
};

// `wrap` is for the narrow places, chiefly a board card. Pills are nowrap by
// default because in a table a wrapped pill makes the row taller than its
// neighbours, but a long free-text value as a single unbreakable line pushes
// straight out of a 190px column into the one beside it.
export function Pill({ text, palette, wrap }) {
  if (!text) return <span style={{ color: '#9CA3AF' }}>—</span>;
  const c = palette[text] || { bg: '#F3F4F6', color: '#6B7280' };
  return <span style={{ padding: '2px 7px', borderRadius: 10, background: c.bg, color: c.color, fontWeight: 700, fontSize: 10, whiteSpace: wrap ? 'normal' : 'nowrap', maxWidth: '100%' }}>{text}</span>;
}

// ============ Shared inline styles ============
const NAVY = '#0D1D32';
// The accent styles.css gives `.bg-navy` in dark mode. Navy against a dark
// slate card is about 1:1, so anything navy-filled has to swap to this or it
// stops reading as a shape at all. Kept in step with the sheet by a test.
const DARK_NAVY = 'rgb(37 99 235)';

export function lbl(isDark) {
  return { fontSize: 11, fontWeight: 700, color: isDark ? 'rgb(148 163 184)' : '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: 3, marginTop: 8 };
}

// Deliberately sets no background: styles.css already gives dark inputs,
// selects and textareas one, and an inline value would overrule it. The border
// is set here only because it is needed in light mode, so its dark value has
// to match the sheet rather than fight it — which is what it used to do,
// leaving every field in a dark modal outlined in pale grey.
export function fieldStyle(isDark) {
  return { width: '100%', border: `1px solid ${isDark ? 'rgb(71 85 105)' : '#E5E7EB'}`, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'Montserrat,sans-serif', outline: 'none', boxSizing: 'border-box' };
}

export function btnPrimary(isDark) {
  return { padding: '6px 16px', background: isDark ? DARK_NAVY : NAVY, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'Montserrat,sans-serif', fontWeight: 600 };
}
// This screen styles itself with inline objects rather than Tailwind classes,
// so it gets none of the `.dark` overrides in styles.css and has to brand every
// colour itself. The three helpers below are the shared pieces that could not
// do that as plain constants, because an object cannot read a hook. Keeping
// them as functions of the theme is also what lets their contrast be asserted
// in tests/bid-tracker-dark-mode.test.mjs.
export function btnSm(isDark) {
  return {
    padding: '4px 10px', fontSize: 11, borderRadius: 5, cursor: 'pointer',
    fontFamily: 'Montserrat,sans-serif', fontWeight: 600,
    border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`,
    background: isDark ? 'rgb(30 41 59)' : '#fff',
    color: isDark ? 'rgb(203 213 225)' : '#374151',
  };
}

// Selection is navy-filled everywhere on this screen, including the tab strip,
// so it reads the same in both themes; only the unselected half moves.
export function segmentedStyle(isDark, selected) {
  return {
    padding: '7px 14px', fontSize: 12, fontWeight: 600, border: 'none',
    cursor: 'pointer', fontFamily: 'Montserrat,sans-serif',
    background: selected ? NAVY : (isDark ? 'rgb(30 41 59)' : '#fff'),
    color: selected ? '#fff' : (isDark ? 'rgb(203 213 225)' : '#374151'),
  };
}

// The KPI tile's palette, in one place so the dark branch is not scattered
// through the markup. Kept as a plain function rather than inlined so its
// contrast can be asserted: the value is 20px bold (AA large, 3:1) and the
// label is 8px (AA small, 4.5:1), and the muted grey the label used in light
// mode manages only about 3:1 on a dark card.
export function kpiStyle(isDark, { color, active } = {}) {
  return {
    background: isDark ? 'rgb(30 41 59)' : '#fff',
    // Navy is invisible as a ring on a near-navy card, so selection takes the
    // accent styles.css already reserves for that case. It lands around 2.8:1
    // against the card rather than a clean 3:1, which the doubled ring (border
    // plus inset shadow) carries; matching the app is worth more here than
    // clearing that bar with a colour nothing else on the screen uses.
    border: active ? (isDark ? DARK_NAVY : NAVY) : (isDark ? 'rgb(51 65 85)' : '#E5E7EB'),
    value: color || (isDark ? 'rgb(241 245 249)' : NAVY),
    label: isDark ? 'rgb(148 163 184)' : '#6B7280',
  };
}

function Modal({ title, onClose, children, maxWidth = 620 }) {
  const isDark = useDarkMode();
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: isDark ? 'rgb(30 41 59)' : '#fff', borderRadius: 10, width: '100%', maxWidth, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: isDark ? '1px solid rgb(51 65 85)' : 'none' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: isDark ? 'rgb(30 41 59)' : '#fff' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 14, cursor: 'pointer', color: '#6B7280' }}>✕</button>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
      </div>
    </div>
  );
}

// Called as `kpi(...)` from the markup rather than rendered as <Kpi />, which is
// why the tile itself is a component underneath: only a component may read the
// theme with a hook.
function kpi(value, label, color, opts = {}) {
  return <KpiTile value={value} label={label} color={color} {...opts} />;
}

function KpiTile({ value, label, color, onClick, active, sub }) {
  const s = kpiStyle(useDarkMode(), { color, active });
  return (
    <div onClick={onClick} style={{ flex: 1, minWidth: 90, background: s.background, border: `1px solid ${s.border}`, boxShadow: active ? `inset 0 0 0 1px ${s.border}` : '0 1px 2px rgba(13,29,50,0.04)', borderRadius: 10, padding: '12px 14px', cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: s.label, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: s.value, marginTop: 2, lineHeight: 1.15 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: s.label, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

// ============ Deadline / urgency helpers ============
// Dates on this screen are UTC days. The urgency rule itself is bidUrgency in
// utils/bidTracker.js, where it is tested; the tones below are the pill it
// draws. The pipeline is ordered attention-first on a real ISO `deadline`; the
// old free-text `key_date` is shown but drives nothing.
function parseDeadline(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(s + 'T00:00:00Z');
  return isNaN(d) ? null : d;
}
function todayUTC() { const n = new Date(); return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate())); }
function daysTo(v) { const d = parseDeadline(v); return d ? Math.round((d - todayUTC()) / 86400000) : null; }
// Through fmtDeadlineWithTime's fixed month table, since toLocaleDateString
// gives "Sept" in some browsers and "Sep" in others.
export function fmtDeadline(v) {
  return parseDeadline(v) ? fmtDeadlineWithTime(String(v).slice(0, 10)) : (v || '—');
}
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const URGENCY_TONES = {
  red:    { bg: '#FEF2F2', color: '#EF4444' },
  orange: { bg: '#FFF7ED', color: '#F97316' },
  grey:   { bg: '#F3F4F6', color: '#6B7280' },
};
// The pill for a bid still being written, or null. A key of 'ok' (more than
// seven days out) is kept for sorting and draws nothing.
export function bidUrgency(b) {
  const u = urgencyOf(b, new Date());
  return u && { ...u, ...(URGENCY_TONES[u.tone] || { bg: '#F0FDF4', color: '#22C55E' }) };
}
// The server's attention flags (bidReminders.attentionFor) decide: due soon,
// no next action, a chase due, a portal unchecked. The same rules drive the
// morning digest and the sidebar badge, so the screen and the email agree.
function needsAttention(b) {
  if (!OPEN_STATUSES.includes(b.status)) return false;
  return !!(b.attention && b.attention.any);
}
const clientNameOf = (b) => b.client_name || b.buyer || '';
// "3 Sep" style for a last touch, or a dash.
export function fmtTouch(v) {
  if (!v) return '—';
  const d = new Date(String(v).includes('T') ? v : String(v).replace(' ', 'T') + (String(v).length > 10 ? 'Z' : 'T00:00:00Z'));
  return isNaN(d) ? String(v).slice(0, 10) : `${String(d.getUTCDate()).padStart(2, '0')} ${SHORT_MONTHS[d.getUTCMonth()]}`;
}
// Sort key: overdue and due today first, then due soon, later, undated, then
// submitted bids (waiting on the buyer), then decided ones.
function attentionRank(b) {
  if (!OPEN_STATUSES.includes(b.status)) return 5;
  const u = bidUrgency(b);
  if (!u) return 4;
  return { overdue: 0, due: 0, soon: 1, ok: 2, watch: 3, nodate: 3 }[u.key] ?? 4;
}
function UrgencyBadge({ b }) {
  const u = bidUrgency(b);
  if (!u || u.key === 'ok') return null;
  return <span style={{ padding: '1px 6px', borderRadius: 8, background: u.bg, color: u.color, fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap' }}>{u.label}</span>;
}
const ownerNameOf = (b, pMap) => (b.owner_id && pMap.get(b.owner_id)) || b.owner || '';
// Today as YYYY-MM-DD, UTC like every other date on this screen.
const todayIso = () => todayUTC().toISOString().slice(0, 10);

// A Responded bid nobody has moved or checked on for more than 30 days. The
// rule is goneQuiet in utils/bidValue.js; this is only its badge.
export function GoneQuietBadge({ b }) {
  if (!goneQuiet(b)) return null;
  return <span title="Responded more than 30 days ago with no portal check since" style={{ padding: '1px 6px', borderRadius: 8, background: '#F3E8FF', color: '#7E22CE', fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap' }}>Gone quiet</span>;
}

// "Hide values" for screen-sharing, from the saved view. The tracker provides
// it; anything that draws a figure reads it, so nothing slips through.
export const HideValuesContext = createContext(false);

// The formatted value with its qualifier badges. The legacy free-text value
// is retired (migration 0407 folded it into value_gbp or the timeline).
export function ValueCell({ b, size = 11 }) {
  const hide = useContext(HideValuesContext);
  if (b.value_gbp == null) return <span style={{ color: '#9CA3AF' }} title="No value recorded">No value</span>;
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <span style={{ fontWeight: 700, fontSize: size }}>{maskMoney(formatValueGbp(b.value_gbp), hide)}</span>
      {valueBadges(b).map(t => <span key={t} style={{ marginLeft: 3, padding: '1px 5px', borderRadius: 8, background: '#F3F4F6', color: '#6B7280', fontWeight: 700, fontSize: 9 }}>{t}</span>)}
    </span>
  );
}

// Initials for the small badge naming who a next action is on.
export const initialsOf = (name) => String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

// The Next line on a card and a row: who it is on (initials, with the name on
// hover), the step, and its date, red once it has passed. Without a next
// action it says so in red. `people` is a Map of id to name, when to hand.
export function NextLine({ b, people, clamp }) {
  if (String(b.next_action || '').trim()) {
    const who = people ? people.get(actionOwnerId(b)) : null;
    const late = b.next_action_due && String(b.next_action_due).slice(0, 10) < todayIso();
    return (
      <div className="text-[11px] text-gray-600 flex items-start gap-1.5 min-w-0">
        {who
          ? <span title={`Next action on ${who}`} className="flex-shrink-0 inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-navy text-white text-[8.5px] font-bold">{initialsOf(who)}</span>
          : <b className="text-gray-800 flex-shrink-0">Next:</b>}
        <span className={`min-w-0 leading-[18px] ${clamp ? 'line-clamp-2' : 'truncate'}`}>
          {b.next_action}
          {b.next_action_due ? <span className={late ? 'text-red-600 font-semibold' : 'text-gray-400'}> · {late ? 'overdue ' : ''}{fmtDeadline(b.next_action_due)}</span> : null}
        </span>
      </div>
    );
  }
  if (!OPEN_STATUSES.includes(b.status)) return null;
  return <div className="text-[11px] font-bold text-red-600">Next: none set</div>;
}

// The badge for a bid submitted six months ago and still waiting (the server's
// long_wait flag): the prompt to decide whether to close it.
export function LongWaitBadge({ b }) {
  if (!b.attention?.long_wait) return null;
  return <span title="Submitted six months or more ago with no decision. Consider closing it." style={{ padding: '1px 7px', borderRadius: 8, background: '#FEF3C7', color: '#92400E', fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap' }}>6+ months</span>;
}

// ============ Board helpers ============
// The board is the Commercial Pipeline kanban redrawn from `bids`. The two are
// separate records by design (migration 0192), so this could not be the same
// screen pointed at another endpoint; what carries across is the shape — a
// column per stage, a card per item, a value total in the column header.
//
// Columns are the bid workflow, so Upcoming leads (it is a real stage here, not
// a separate list as on the Pipeline tab) and Won closes the board. Lost,
// No-bid and Parked are collapsed underneath, the way the Pipeline board keeps
// Not Won and Cancelled out of the columns.
export const BOARD_STAGES = [
  { key: 'Upcoming',      label: 'Upcoming',      color: '#4F46E5' },
  { key: 'Evaluating',    label: 'Evaluating',    color: '#F97316' },
  { key: 'Responding',    label: 'Responding',    color: '#D97706' },
  { key: 'Responded',     label: 'Responded',     color: '#A16207' },
  { key: 'Awaiting decision', label: 'Awaiting decision', color: '#1D4ED8' },
  { key: 'In Discussion', label: 'In Discussion', color: '#0891B2' },
  { key: 'Won',           label: 'Won',           color: '#22C55E' },
];
export const BOARD_OUTCOMES = [
  { key: 'Lost',   label: 'Lost',   color: '#EF4444' },
  { key: 'No-bid', label: 'No-bid', color: '#6B7280' },
  { key: 'Parked', label: 'Parked', color: '#6B7280' },
];

// Values: a confirmed value_gbp where there is one (inc VAT brought back to ex
// VAT, framework ceilings counted rather than added), else the legacy
// est_value text where it is actually a figure. parseEstValue and valueTotals
// live in utils/bidValue.js, where they are run by tests/bid-value.test.mjs;
// anything else counts as unpriced rather than being folded in as zero, which
// would understate the column and say so nowhere.

// Soonest deadline first, undated last, then by id so the order is stable
// rather than however the rows happened to arrive.
const boardOrder = (a, b) => deadlineSort(a, b);

export function buildBoard(bids) {
  const rows = Array.isArray(bids) ? bids : [];
  const columns = BOARD_STAGES.map(st => {
    const inStage = rows.filter(b => b.status === st.key).sort(boardOrder);
    return { ...st, bids: inStage, ...valueTotals(inStage) };
  });
  const outcomes = BOARD_OUTCOMES.map(g => {
    const inGroup = rows.filter(b => b.status === g.key).sort(boardOrder);
    return { ...g, bids: inGroup, ...valueTotals(inGroup) };
  });
  // A status in neither list — an 'Active' row on a database that predates
  // migration 0242, say — goes under Other. A board that silently dropped it
  // would under-report the pipeline with nothing on screen to say so.
  const known = new Set([...BOARD_STAGES, ...BOARD_OUTCOMES].map(s => s.key));
  const other = rows.filter(b => !known.has(b.status)).sort(boardOrder);
  if (other.length) outcomes.push({ key: 'Other', label: 'Unrecognised status', color: '#6B7280', bids: other, ...valueTotals(other) });
  return { columns, outcomes };
}

// Column totals are approximate by nature, so they are shown rounded rather
// than to the penny: "£1.25m", "£250k".
export function fmtBoardValue(n) {
  if (n == null) return '';
  if (n >= 1e6) return '£' + (n / 1e6).toFixed(n % 1e6 === 0 ? 0 : 2).replace(/\.?0+$/, '') + 'm';
  if (n >= 1e3) return '£' + Math.round(n / 1e3) + 'k';
  return '£' + Math.round(n);
}

// ============ Main page ============
export default function BidTrackerPage() {
  const isDark = useDarkMode();
  const { currentUser } = useContext(AppContext);
  const isAdmin = (currentUser?.user_role || '').split(',').map(r => r.trim()).includes('admin');

  // Pipeline | Triage | My bids | Bids | Review | Buyers | Reports. The CRM
  // redesign of 29 September 2026 set the tabs; the streamlining of 25 October
  // 2026 added Triage (most notices are decided against, so deciding them is
  // its own quick job) and folded Contacts into Buyers as its People view.
  const [tab, setTab] = useState('pipeline');
  // Every row, tenders and leads (migration 0417). The tender tabs read bids
  // only, so a lead never lands in the Pipeline, Triage, Bids or Reports
  // figures; Leads reads leads; Review reads both.
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [people, setPeople] = useState([]);
  const [clients, setClients] = useState([]);
  const [view, updateView] = useSavedView(VIEW_PAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [creating, setCreating] = useState(null);   // null | 'bid' | 'lead'
  const { navigate } = useApp();
  // The guided tour opens once per person after the page has loaded; the ?
  // by the title replays it. The steps switch tabs through the tab anchors.
  const tour = useFeatureTour(BID_TRACKER_TOUR_ID, { enabled: !loading && !error });

  const showToast = (msg, kind = 'ok') => { setToast({ msg, kind }); setTimeout(() => setToast(null), 3200); };

  // Always resolve loading (and surface an error) — never leave the page stuck on "Loading…"
  // if the API call fails (e.g. the bids migration hasn't been applied to this environment).
  // A reload after a change is quiet: the tab stays mounted, so whatever it
  // was saying about the change (a triage result, a refusal on a card) stays
  // on screen where the person acted, and the page does not flash to Loading.
  const load = async ({ quiet = false } = {}) => {
    if (!quiet) { setLoading(true); setError(null); }
    try {
      const [bidData, colData, peopleData, clientData] = await Promise.all([
        api.get('crm/bids'),
        api.get('crm/columns').catch(() => ({ rows: [] })),
        api.get('people').catch(() => []),
        api.get('clients').catch(() => []),
      ]);
      setRows(bidData?.rows || []);
      setColumns(colData?.rows || []);
      setPeople(Array.isArray(peopleData) ? peopleData : (peopleData?.rows || []));
      setClients(Array.isArray(clientData) ? clientData : (clientData?.clients || clientData?.rows || []));
    } catch (e) {
      if (quiet) showToast(e?.message || 'Could not refresh the bid tracker.', 'error');
      else setError(e?.message || 'Failed to load the bid tracker.');
    } finally {
      if (!quiet) setLoading(false);
    }
  };
  const reload = () => load({ quiet: true });
  useEffect(() => { load(); }, []);

  const { bids, leads } = splitByKind(rows);
  const mineNeedingAttention = rows.filter(b => currentUser && b.owner_id === currentUser.id && needsAttention(b)).length;
  const toTriage = triageQueue(bids).length;
  const openLeads = leads.filter(b => OPEN_STATUSES.includes(b.status)).length;
  const TABS = [
    { key: 'pipeline', label: 'Pipeline' },
    { key: 'triage', label: 'Triage', count: toTriage },
    { key: 'mine', label: 'My bids' },
    { key: 'bids', label: 'Bids', count: bids.length },
    { key: 'leads', label: 'Leads', count: openLeads },
    { key: 'review', label: 'Review', count: mineNeedingAttention },
    { key: 'buyers', label: 'Buyers' },
    { key: 'report', label: 'Reports' },
  ];
  // A Pipeline tile opens Bids on the matching filter.
  const openBids = (quick, extra = {}) => {
    updateView(v => ({ ...v, filters: { ...v.filters, quick, status: '', ...extra } }));
    setTab('bids');
  };
  const shared = { bids, leads, columns, people, clients, setClients, currentUser, isAdmin, reload, setColumns, showToast, view, updateView, openBids, setTab };

  return (
    <HideValuesContext.Provider value={!!view.hideValues}>
    <div style={{ padding: '16px 20px' }}>
      {/* bid-tracker-chrome: hidden by the print stylesheet when the Reports
          tab prints, so the page carries only the report sections. */}
      <div className="bid-tracker-chrome" style={{ marginBottom: 16 }} data-tour="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: isDark ? 'rgb(241 245 249)' : '#111827', margin: 0 }}>Pipeline Tracker</h1>
          <button onClick={tour.start} aria-label="Replay the guided tour of this page" title="Replay the guided tour of this page"
            style={{ width: 22, height: 22, borderRadius: 11, border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, background: 'none', color: '#6B7280', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Montserrat,sans-serif' }}>?</button>
          <span style={{ flex: 1 }} />
          <label className="text-[12px] text-gray-600 flex items-center gap-1.5 cursor-pointer" title="Masks every figure, for sharing your screen" data-tour="hide-values">
            <input type="checkbox" checked={!!view.hideValues} onChange={e => updateView({ hideValues: e.target.checked })} /> Hide values
          </label>
          <button onClick={() => setCreating('lead')} style={btnSm(isDark)} data-tour="new-lead" title="A referral, enquiry or MSP conversation that is not a formal tender">+ New lead</button>
          <button onClick={() => setCreating('bid')} style={btnPrimary(isDark)} data-tour="new-bid">+ New bid</button>
        </div>
        {/* The tabs on a row of their own, so the strip never pushes New bid
            onto a second line however many counts it carries. */}
        <div style={{ display: 'inline-flex', flexWrap: 'wrap', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 8, overflow: 'hidden' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-tour={`tab-${t.key}`} aria-pressed={tab === t.key} style={segmentedStyle(isDark, tab === t.key)}>
              {t.label}
              {t.count != null && <span style={{ marginLeft: 6, padding: '0 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: tab === t.key ? 'rgba(255,255,255,0.18)' : (isDark ? 'rgb(51 65 85)' : '#F3F4F6') }}>{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 24, fontSize: 13, color: '#6B7280' }}>Loading…</div>
      ) : error ? (
        <div style={{ padding: 24, fontSize: 13, color: '#EF4444' }}>
          {error}
          <button onClick={load} style={{ ...btnSm(isDark), marginLeft: 10 }}>Retry</button>
        </div>
      ) : (
        <>
          {tab === 'pipeline' && <PipelineTab {...shared} />}
          {tab === 'triage' && <TriageTab {...shared} />}
          {tab === 'mine' && <MyBidsTab {...shared} />}
          {tab === 'bids' && <BidsTab {...shared} />}
          {tab === 'leads' && <LeadsTab {...shared} onNew={() => setCreating('lead')} />}
          {tab === 'review' && <ReviewTab {...shared} bids={rows} />}
          {tab === 'buyers' && <BuyersTab {...shared} />}
          {tab === 'report' && <ReportTab bids={bids} />}
        </>
      )}

      {creating && (
        <BidForm kind={creating} columns={columns} people={people} clients={clients} onClose={() => setCreating(null)}
          onSaved={(row) => { const k = creating; setCreating(null); reload(); showToast(k === 'lead' ? 'Lead created' : 'Bid created'); if (row?.id) navigate('bid-detail', row.id); }}
          onError={m => showToast(m, 'error')} />
      )}

      {tour.open && <FeatureTour steps={bidTrackerTourSteps} onFinish={tour.finish} onSkip={tour.skip} />}

      {toast && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 2000, background: toast.kind === 'error' ? '#EF4444' : NAVY, color: '#fff', padding: '10px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
          {toast.msg}
        </div>
      )}
    </div>
    </HideValuesContext.Provider>
  );
}

// ============ Pipeline tab ============
// Five tiles over the board. Each opens the view behind its number.
function PipelineTab(props) {
  const { bids, people, currentUser, view, openBids, setTab } = props;
  const k = pipelineKpis(bids, currentUser?.id, new Date());
  const pipe = k.pipeline;
  const pipeValue = pipe.value == null ? '—' : maskMoney(fmtBoardValue(pipe.value), !!view.hideValues);
  const [focus, setFocus] = useState(null);
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 10, marginBottom: 14 }} data-tour="pipeline-kpis">
        {kpi(k.open, 'Open bids', null, { sub: 'across every owner', onClick: () => openBids('open') })}
        {kpi(k.needYou, 'Need you', '#EF4444', { sub: 'your bids needing attention', onClick: () => setTab('mine') })}
        {kpi(k.due14, 'Due within 14 days', '#F97316', { sub: 'not yet submitted', onClick: () => openBids('due14', { sort: 'asc' }) })}
        {kpi(k.waiting, 'Waiting on a buyer', '#1D4ED8', { sub: `${k.quiet} gone quiet`, onClick: () => openBids('waiting') })}
        {kpi(pipeValue, 'Open pipeline ex VAT', null, { sub: pipe.ceilings ? `${pipe.ceilings} framework ceiling${pipe.ceilings === 1 ? '' : 's'} not added` : 'contract values only', onClick: () => openBids('open') })}
      </div>
      <WaitingOn bids={bids} people={people} focus={focus} setFocus={setFocus} />
      <BidBoard {...props} focus={focus} />
    </div>
  );
}

// Who the open bids are waiting on: one chip per person with a next action,
// busiest first, overdue ones counted in red. Picking a person narrows the
// board to the bids waiting on them, so a bottleneck shows at a glance.
function WaitingOn({ bids, people, focus, setFocus }) {
  const pMap = new Map((people || []).map(p => [p.id, `${p.first_name || ''} ${p.last_name || ''}`.trim()]));
  const rows = waitingOn(bids, new Date());
  if (!rows.length) return null;
  const chip = (on) => `inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border text-[11.5px] font-semibold ${on ? 'bg-navy text-white border-navy' : 'bg-white text-gray-700 border-gray-200 hover:border-gold'}`;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-3" data-tour="waiting-on">
      <span className="text-[10.5px] font-bold uppercase tracking-wider text-gray-500 mr-1">Waiting on</span>
      {rows.map(r => {
        const name = pMap.get(r.personId) || 'Someone';
        const on = focus === r.personId;
        return (
          <button key={r.personId} type="button" onClick={() => setFocus(on ? null : r.personId)} aria-pressed={on} className={chip(on)}
            title={`${r.count} next action${r.count === 1 ? '' : 's'} on ${name}${r.overdue ? `, ${r.overdue} overdue` : ''}. Click to show only these on the board.`}>
            <span className={`inline-flex items-center justify-center w-[20px] h-[20px] rounded-full text-[9px] font-bold ${on ? 'bg-white text-navy' : 'bg-navy text-white'}`}>{initialsOf(name)}</span>
            {name}
            <span className={on ? 'opacity-80' : 'text-gray-500'}>{r.count}</span>
            {r.overdue > 0 && <span className="px-1.5 rounded-full bg-red-50 text-red-700 text-[10px] font-bold">{r.overdue} overdue</span>}
          </button>
        );
      })}
      {focus && <button type="button" onClick={() => setFocus(null)} className="text-[11px] font-semibold text-gray-500 hover:text-navy underline ml-1">Show everyone</button>}
    </div>
  );
}

// ============ Board (kanban) ============
// A column per stage, compact cards, count and value in the header. The
// columns wrap into rows on a narrow pane rather than scrolling sideways (the
// redesign's rule: nothing on this page scrolls sideways at any width).
const BOARD_PAGE_SIZE = 8;

function BidBoard({ bids: all, people, reload, showToast, view, updateView, focus }) {
  const { navigate } = useApp();
  const pMap = new Map((people || []).map(p => [p.id, `${p.first_name || ''} ${p.last_name || ''}`.trim()]));
  // The Waiting on strip narrows the board to the bids whose next action is
  // on one person; the outcome groups below stay whole.
  const bids = focus ? all.filter(b => !OPEN_STATUSES.includes(b.status) || (String(b.next_action || '').trim() && actionOwnerId(b) === focus)) : all;
  const { columns: stages, outcomes } = buildBoard(bids);
  const byKey = new Map([...stages, ...outcomes].map(c => [c.key, c]));
  const cols = boardColumns(!!view.outcomeCols).map(k => byKey.get(k)).filter(Boolean);
  const hide = !!view.hideValues;
  const [showAll, setShowAll] = useState({});
  const [openGroup, setOpenGroup] = useState(null);
  const [moving, setMoving] = useState(null);
  const [refusal, setRefusal] = useState(null);   // { id, msg }: shown on that card
  const [outcome, setOutcome] = useState(null);   // { bid, target }

  // Same contract as the bid page's stepper: stamp the stage being left so a
  // bid moved by mistake can be reopened to where it was. Won, Lost and No-bid
  // need a result date (and a no-bid a reason), so they open the outcome form.
  const moveTo = async (bid, status) => {
    if (status === bid.status || moving) return;
    setRefusal(null);
    if (['Won', 'Lost', 'No-bid'].includes(status)) { setOutcome({ bid, target: status }); return; }
    setMoving(bid.id);
    try {
      await api.put(`crm/bids/${bid.id}`, { status, previous_status: bid.status });
      await reload();
      showToast(`${bid.opportunity} moved to ${status}`);
    } catch (e) {
      setRefusal({ id: bid.id, msg: e?.message || 'Could not move this bid.' });
    } finally { setMoving(null); }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 8 }} data-tour="board">
        {cols.map(col => {
          const shown = showAll[col.key] ? col.bids : col.bids.slice(0, BOARD_PAGE_SIZE);
          return (
            <div key={col.key} className="bg-gray-50 border border-gray-200 rounded-xl min-w-0 flex flex-col">
              {/* The stage's colour is a rule across the top; the label stays
                  dark so it reads in both themes (a pale tint with coloured
                  text fell below 4.5:1 for Responded and Won). */}
              <div className="text-gray-700" style={{ padding: '8px 10px 6px', borderRadius: '12px 12px 0 0', borderTop: '3px solid ' + col.color, fontSize: 11, fontWeight: 800, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '2px 6px' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {col.label}
                  <span className="bg-white border border-gray-200 text-gray-600" style={{ borderRadius: 9, padding: '0 7px', fontSize: 10, fontWeight: 700 }}>{col.bids.length}</span>
                </span>
                <span className="text-gray-500" style={{ fontWeight: 600, fontSize: 10.5 }} title={col.value != null ? 'Contract values only, ex VAT' : undefined}>
                  {col.value != null ? `${maskMoney(fmtBoardValue(col.value), hide)} ex VAT` : ''}
                  {col.ceilings > 0 && <span style={{ opacity: 0.7 }}>{col.value != null ? ' +' : ''}{col.ceilings} framework ceiling{col.ceilings === 1 ? '' : 's'}</span>}
                  {col.unpriced > 0 && <span style={{ opacity: 0.7 }}>{col.value != null || col.ceilings > 0 ? ' +' : ''}{col.unpriced} unpriced</span>}
                </span>
              </div>
              <div className="p-2 flex flex-col gap-2" style={{ minHeight: 60 }}>
                {shown.map(b => (
                  <BoardCard key={b.id} bid={b} ownerName={ownerNameOf(b, pMap)} people={pMap} moving={moving === b.id}
                    refusal={refusal && refusal.id === b.id ? refusal.msg : null}
                    onOpen={() => navigate('bid-detail', b.id)} onMove={s => moveTo(b, s)} />
                ))}
                {col.bids.length === 0 && <div className="text-[11px] text-gray-400 text-center py-3">{focus ? 'Nothing on them here' : 'Empty'}</div>}
                {col.bids.length > BOARD_PAGE_SIZE && (
                  <button type="button" onClick={() => setShowAll(p => ({ ...p, [col.key]: !p[col.key] }))} className="text-[11px] font-semibold text-navy hover:underline py-0.5">
                    {showAll[col.key] ? 'Show fewer' : `Show ${col.bids.length - BOARD_PAGE_SIZE} more`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Decided and parked bids, off the board but one click away, each with
          its stage line (the summary) so the outcome reads without opening it. */}
      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
        {outcomes.map(g => (
          <button key={g.key} type="button" onClick={() => setOpenGroup(openGroup === g.key ? null : g.key)} aria-expanded={openGroup === g.key}
            className={`px-2.5 py-1 text-[11px] font-bold rounded-md border ${openGroup === g.key ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {openGroup === g.key ? '▴' : '▾'} {g.label} ({g.bids.length})
          </button>
        ))}
        <span className="flex-1" />
        <label className="text-[11px] text-gray-600 flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={!!view.outcomeCols} onChange={e => updateView({ outcomeCols: e.target.checked })} /> Include outcome columns on the board
        </label>
      </div>
      {openGroup && (() => {
        const g = outcomes.find(x => x.key === openGroup);
        return (
          <div className="mt-2 bg-white border border-gray-200 rounded-lg">
            {!g || g.bids.length === 0 ? <div className="p-3.5 text-[11px] text-gray-400">Nothing here.</div> : g.bids.map(b => (
              <div key={b.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 px-3 py-2 border-t border-gray-100 first:border-t-0">
                <div className="min-w-0">
                  <div className="text-[12px]"><span className="font-mono text-[11px] font-bold text-gold mr-1.5">{b.reference || `#${b.id}`}</span>
                    <button type="button" onClick={() => navigate('bid-detail', b.id)} className="font-bold text-gray-900 hover:text-gold text-left">{b.opportunity}</button></div>
                  <div className="text-[11px] text-gray-500">{[clientNameOf(b) || 'No client', ownerNameOf(b, pMap)].filter(Boolean).join(' · ')}</div>
                  <div className="text-[11px] text-gray-600 mt-0.5 whitespace-pre-wrap">{b.summary || b.no_bid_reason || 'No stage line recorded.'}</div>
                </div>
                <div className="flex flex-col items-end gap-1 text-[12px]">
                  <ValueCell b={b} />
                  {b.result ? <Pill text={b.result} palette={RESULT_COLORS} /> : <Pill text={b.status} palette={STATUS_COLORS} />}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {outcome && (
        <OutcomeForm b={outcome.bid} target={outcome.target} onClose={() => setOutcome(null)}
          onSaved={(res) => { setOutcome(null); reload(); outcomeToast(showToast, res); }} onError={m => showToast(m, 'error')} />
      )}
    </div>
  );
}

// Tones for the card's left edge: how soon the bid has to go in.
const EDGE = { red: '#EF4444', orange: '#F97316' };

function BoardCard({ bid, ownerName, people, moving, refusal, onOpen, onMove }) {
  const isDark = useDarkMode();
  const [menuPos, setMenuPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Rendered into a portal at viewport coordinates, so a column's bounds can
  // never clip the menu. It closes on scroll rather than following the trigger.
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setMenuPos({ top: r.bottom + 2, left: Math.max(8, r.right - 160) });
  };
  useEffect(() => {
    if (!menuPos) return;
    const onDown = e => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setMenuPos(null);
    };
    const close = () => setMenuPos(null);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuPos]);

  const allStatuses = [...BOARD_STAGES, ...BOARD_OUTCOMES];
  const u = bidUrgency(bid);
  const edge = u && EDGE[u.tone];

  return (
    <div onClick={onOpen} className={`bg-white border border-gray-200 rounded-lg px-2.5 py-2 cursor-pointer min-w-0 transition-shadow hover:border-gold hover:shadow-md motion-reduce:transition-none ${moving ? 'opacity-50' : ''}`}
      style={edge ? { boxShadow: `inset 3px 0 0 ${edge}` } : undefined}>
      <div className="flex items-center justify-between gap-1 min-w-0">
        <span className="font-mono text-[10.5px] font-bold text-gold truncate">{bid.reference || `#${bid.id}`}</span>
        <span className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
          <UrgencyBadge b={bid} /><GoneQuietBadge b={bid} /><LongWaitBadge b={bid} />
          <button ref={btnRef} type="button" onClick={() => (menuPos ? setMenuPos(null) : openMenu())} aria-label="Move to another stage" title="Move to another stage" aria-expanded={!!menuPos}
            className="w-[22px] h-[22px] inline-flex items-center justify-center rounded text-gray-400 hover:text-navy hover:bg-gray-100 text-[14px] leading-none">⋯</button>
        </span>
      </div>
      <div className="text-[12.5px] font-bold text-gray-900 mt-0.5 leading-snug">{bid.opportunity}</div>
      <div className="text-[10.5px] text-gray-500 truncate mt-0.5">{clientNameOf(bid) || 'No client'}{ownerName ? ' · ' + ownerName : ''}</div>
      <div className="flex items-center justify-between gap-2 mt-1.5 flex-wrap min-w-0">
        <span className="text-[12px]"><ValueCell b={bid} size={12} /></span>
        {bid.key_date && bid.key_date >= todayIso() && (
          <span className="text-[10px] text-gray-500 whitespace-nowrap" title={bid.key_date_label || 'Key date'}>{bid.key_date_label ? `${bid.key_date_label} ` : ''}{fmtTouch(bid.key_date)}</span>
        )}
      </div>
      <div className="mt-1.5 pt-1.5 border-t border-gray-100"><NextLine b={bid} people={people} clamp /></div>
      {refusal && <div role="alert" className="mt-1.5 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1">{refusal}</div>}
      {menuPos && createPortal(
        <div ref={menuRef} onClick={e => e.stopPropagation()} style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 2000, minWidth: 160, background: isDark ? 'rgb(30 41 59)' : '#fff', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', overflow: 'hidden', padding: '4px 0' }}>
          <div style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? 'rgb(148 163 184)' : '#6B7280' }}>Move to</div>
          {allStatuses.map(s => (
            <button key={s.key} onClick={() => { setMenuPos(null); onMove(s.key); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '6px 10px', fontSize: 12, border: 'none', cursor: 'pointer', fontFamily: 'Montserrat,sans-serif', background: 'transparent', color: isDark ? 'rgb(203 213 225)' : '#374151', fontWeight: bid.status === s.key ? 800 : 500 }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: s.color, flexShrink: 0 }} />
              {s.label}
              {bid.status === s.key && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#A1723D' }}>✓</span>}
            </button>
          ))}
        </div>, document.body)}
    </div>
  );
}

// ============ Triage tab ============
// Every notice still at Upcoming, soonest first, waiting for one decision:
// evaluate it, or close it. Most are closed (in October 2026, 30 of the 32
// decided were no-bids), so closing is one click and a reason, for one bid or
// for several at once (POST crm/bids/bulk-close, the same rules as a single
// move). What happened is said in the bar above the list, where the person
// acted, and a row that could not be closed is named there with the reason.
function TriageTab({ bids, people, reload, view }) {
  const { navigate } = useApp();
  const pMap = new Map((people || []).map(p => [p.id, `${p.first_name || ''} ${p.last_name || ''}`.trim()]));
  const queue = triageQueue(bids);
  const [picked, setPicked] = useState(() => new Set());
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null);       // 'bulk' or a bid id
  const [result, setResult] = useState(null);   // { tone, text, skipped }
  const [noBidFor, setNoBidFor] = useState(null); // the row whose reason picker is open
  const [rowReason, setRowReason] = useState('');
  const [rowErr, setRowErr] = useState(null);   // { id, text }
  const hide = !!view.hideValues;
  useEffect(() => {
    api.get('config-options', { category: 'bid_no_bid_reason' })
      .then(d => setReasons(Array.isArray(d) ? d.filter(o => o.is_active !== 0) : [])).catch(() => {});
  }, []);

  const toggle = (id) => setPicked(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allPicked = queue.length > 0 && queue.every(b => picked.has(b.id));
  const say = (closed, status, skipped) => setResult({
    tone: skipped.length ? 'warn' : 'ok',
    text: `${closed} bid${closed === 1 ? '' : 's'} ${status === 'Parked' ? 'parked' : 'closed as No-bid'}.${skipped.length ? ` ${skipped.length} could not be closed:` : ''}`,
    skipped,
  });
  const closeMany = async (status) => {
    setBusy('bulk'); setResult(null);
    try {
      const out = await api.post('crm/bids/bulk-close', { ids: [...picked], status, no_bid_reason: status === 'No-bid' ? reason : undefined });
      setPicked(new Set());
      say(out.closed.length, status, out.skipped || []);
      await reload();
    } catch (e) { setResult({ tone: 'warn', text: e?.message || 'Could not close those bids.', skipped: [] }); }
    finally { setBusy(null); }
  };
  const closeOne = async (b, status, why) => {
    setBusy(b.id); setRowErr(null);
    try {
      const out = await api.post('crm/bids/bulk-close', { ids: [b.id], status, no_bid_reason: why });
      if (out.skipped?.length) { setRowErr({ id: b.id, text: out.skipped[0].reason }); return; }
      setNoBidFor(null);
      setResult({ tone: 'ok', text: `${b.reference || b.opportunity} ${status === 'Parked' ? 'parked' : 'closed as No-bid'}.`, skipped: [] });
      await reload();
    } catch (e) { setRowErr({ id: b.id, text: e?.message || 'Could not close this bid.' }); }
    finally { setBusy(null); }
  };
  const evaluate = async (b) => {
    setBusy(b.id); setRowErr(null);
    try {
      await api.put(`crm/bids/${b.id}`, { status: 'Evaluating', previous_status: b.status });
      setResult({ tone: 'ok', text: `${b.reference || b.opportunity} moved to Evaluating.`, skipped: [] });
      await reload();
    } catch (e) { setRowErr({ id: b.id, text: e?.message || 'Could not move this bid.' }); }
    finally { setBusy(null); }
  };

  const btn = 'px-2.5 py-1 text-[11px] font-semibold rounded-md border disabled:opacity-50';
  const reasonSelect = (value, onChange, label) => (
    <select value={value} onChange={e => onChange(e.target.value)} aria-label={label} className="form-select text-[11.5px]" style={{ width: 'auto', flex: '0 1 auto' }}>
      <option value="">No-bid reason…</option>
      {reasons.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  return (
    <div data-tour="triage">
      <div className="text-[12px] text-gray-600 mb-3 max-w-3xl">
        New notices land here at Upcoming. Decide each one: <b className="text-gray-800">Evaluate</b> it to start the bid, or close it as a <b className="text-gray-800">No-bid</b> with a reason. Tick several to close them together.
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 border-b border-gray-200 bg-gray-50" data-tour="triage-bulk">
          <label className="flex items-center gap-1.5 text-[11.5px] font-semibold text-gray-700 cursor-pointer">
            <input type="checkbox" checked={allPicked} disabled={!queue.length} onChange={() => setPicked(allPicked ? new Set() : new Set(queue.map(b => b.id)))} />
            {picked.size ? `${picked.size} selected` : `${queue.length} to decide`}
          </label>
          {picked.size > 0 && (
            <>
              {reasonSelect(reason, setReason, 'No-bid reason for the selected bids')}
              <button type="button" onClick={() => closeMany('No-bid')} disabled={busy === 'bulk' || !reason} title={reason ? undefined : 'Pick the no-bid reason first'} className={`${btn} bg-navy text-white border-navy`}>{busy === 'bulk' ? 'Closing…' : `No-bid ${picked.size}`}</button>
              <button type="button" onClick={() => closeMany('Parked')} disabled={busy === 'bulk'} className={`${btn} bg-white text-gray-700 border-gray-200 hover:border-gold`}>Park {picked.size}</button>
              <button type="button" onClick={() => setPicked(new Set())} className="text-[11px] text-gray-500 hover:text-navy underline">Clear</button>
            </>
          )}
          {result && (
            <div role="status" className={`basis-full text-[11.5px] rounded-md px-2.5 py-1.5 ${result.tone === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
              {result.text}
              {result.skipped.length > 0 && <ul className="mt-0.5 list-disc pl-4">{result.skipped.map(k => <li key={k.id}>{k.reference || `#${k.id}`}: {k.reason}</li>)}</ul>}
            </div>
          )}
        </div>
        {queue.length === 0 ? (
          <div className="p-6 text-center text-[12px] text-gray-400">Nothing to triage. Every notice has been evaluated or closed.</div>
        ) : queue.map(b => {
          const when = b.deadline ? fmtDeadlineWithTime(b.deadline, b.deadline_time) : b.watch_date ? `Watch ${fmtDeadline(b.watch_date)}` : 'No deadline';
          const open = noBidFor === b.id;
          return (
            <div key={b.id} className={`grid grid-cols-[20px_minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 px-3 py-2.5 border-t border-gray-100 first:border-t-0 items-start ${picked.has(b.id) ? 'bg-gold/5' : ''}`}>
              <input type="checkbox" className="mt-1" checked={picked.has(b.id)} onChange={() => toggle(b.id)} aria-label={`Select ${b.opportunity}`} />
              <div className="min-w-0">
                <div className="text-[12.5px]">
                  <span className="font-mono text-[11px] font-bold text-gold mr-1.5">{b.reference || `#${b.id}`}</span>
                  <button type="button" onClick={() => navigate('bid-detail', b.id)} className="font-bold text-gray-900 hover:text-gold text-left">{b.opportunity}</button>
                </div>
                <div className="text-[11px] text-gray-500">{[clientNameOf(b) || 'No client', b.service_line, ownerNameOf(b, pMap)].filter(Boolean).join(' · ')}</div>
                {b.summary && <div className="text-[11px] text-gray-600 mt-0.5 line-clamp-2">{b.summary}</div>}
              </div>
              <div className="flex flex-col items-end gap-1 text-right">
                <div className="text-[12px]">{b.value_gbp == null ? <span className="text-gray-400">No value</span> : <ValueCell b={b} size={12} />}</div>
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500 whitespace-nowrap"><UrgencyBadge b={b} />{when}</div>
              </div>
              <div className="col-start-2 col-span-2 flex flex-wrap items-center gap-1.5">
                <button type="button" onClick={() => evaluate(b)} disabled={busy === b.id} className={`${btn} bg-navy text-white border-navy`}>Evaluate</button>
                {open ? (
                  <>
                    {reasonSelect(rowReason, setRowReason, `No-bid reason for ${b.opportunity}`)}
                    <button type="button" onClick={() => closeOne(b, 'No-bid', rowReason)} disabled={busy === b.id || !rowReason} title={rowReason ? undefined : 'Pick the reason first'} className={`${btn} bg-white text-red-700 border-red-200 hover:bg-red-50`}>{busy === b.id ? 'Closing…' : 'Close as No-bid'}</button>
                    <button type="button" onClick={() => { setNoBidFor(null); setRowErr(null); }} className="text-[11px] text-gray-500 hover:text-navy underline">Keep it open</button>
                  </>
                ) : (
                  <button type="button" onClick={() => { setNoBidFor(b.id); setRowReason(''); setRowErr(null); }} className={`${btn} bg-white text-gray-700 border-gray-200 hover:border-gold`}>No-bid…</button>
                )}
                {!open && <button type="button" onClick={() => closeOne(b, 'Parked')} disabled={busy === b.id} className={`${btn} bg-white text-gray-700 border-gray-200 hover:border-gold`} title="Not now: off the pipeline until it is reopened">Park</button>}
                {rowErr && rowErr.id === b.id && <span role="alert" className="text-[11px] text-red-600">{rowErr.text}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ My bids tab ============
// What I am working on: the things that need me this morning (the same
// reasons the daily digest emails), then my open bids in attention order,
// with the bids owned by someone else whose next action is on me.
function MyBidsTab(props) {
  const { bids, currentUser } = props;
  const isDark = useDarkMode();
  const { navigate } = useApp();
  const mine = bids.filter(b => currentUser && b.owner_id === currentUser.id && OPEN_STATUSES.includes(b.status));
  const onMe = bids.filter(b => currentUser && b.owner_id !== currentUser.id && OPEN_STATUSES.includes(b.status)
    && String(b.next_action || '').trim() && actionOwnerId(b) === currentUser.id);
  const att = (k) => mine.filter(b => b.attention && b.attention[k]);
  const dueNow = att('due_now'), dueSoon = att('due_soon'), noNext = att('no_next_action'), chase = att('chase_due'), portals = att('portal_due'), longWait = att('long_wait');
  const card = (title, items, tone, what) => (
    <div style={{ background: isDark ? 'rgb(30 41 59)' : '#fff', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 10, padding: 14, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 20, fontWeight: 800, color: items.length ? tone : (isDark ? 'rgb(148 163 184)' : '#9CA3AF') }}>{items.length}</span>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: isDark ? 'rgb(148 163 184)' : '#6B7280' }}>{title}</span>
      </div>
      {items.length === 0 ? <div style={{ fontSize: 11, color: '#9CA3AF' }}>Nothing</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {items.slice(0, 6).map(b => (
            <div key={b.id} onClick={() => navigate('bid-detail', b.id)} style={{ cursor: 'pointer', fontSize: 11, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontWeight: 600, color: isDark ? 'rgb(241 245 249)' : '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.opportunity}</span>
              <span style={{ color: '#6B7280', whiteSpace: 'nowrap' }}>{what(b)}</span>
            </div>
          ))}
          {items.length > 6 && <div style={{ fontSize: 10, color: '#9CA3AF' }}>and {items.length - 6} more below</div>}
        </div>
      )}
    </div>
  );
  return (
    <div>
      {!currentUser ? null : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 230px), 1fr))', gap: 10, marginBottom: 16 }} data-tour="attention-cards">
          {card('Due within 3 days', dueNow, '#EF4444', b => fmtDeadline(b.deadline))}
          {card('Due within 14 days', dueSoon, '#F97316', b => fmtDeadline(b.deadline))}
          {card('No next action', noNext, '#B45309', b => `touched ${fmtTouch(b.last_touch_at)}`)}
          {card('Chase the buyer', chase, '#1D4ED8', b => `submitted ${fmtDeadline(b.deadline)}`)}
          {card('Portal unchecked', portals, '#7E22CE', b => b.last_checked_at ? `checked ${fmtTouch(b.last_checked_at)}` : 'never checked')}
          {longWait.length > 0 && card('Waiting 6+ months', longWait, '#92400E', b => `submitted ${fmtDeadline(b.deadline)}`)}
        </div>
      )}
      <div data-tour="my-open-bids">
        <BidRows {...props} rows={mine} title="My open bids" showQuick={false}
          empty={currentUser ? 'You own no open bids. Everything you own is decided, or on Bids under another owner.' : 'Sign in to see your bids.'} />
      </div>
      {onMe.length > 0 && (
        <div className="mt-4">
          <BidRows {...props} rows={onMe} title="Next actions on you, on other people's bids" showQuick={false} />
        </div>
      )}
    </div>
  );
}

// ============ Bids tab ============
// Every bid as rows. The chips are the handoff's quick filters; due14 and
// waiting are reached from the Pipeline tiles only, and show as a removable
// filter rather than a chip.
const QUICK = [
  { value: 'open', label: 'Open', test: b => OPEN_STATUSES.includes(b.status) },
  { value: 'attention', label: 'Needs attention', test: b => needsAttention(b) },
  { value: 'upcoming', label: 'Upcoming', test: b => b.status === 'Upcoming' },
  { value: 'awaiting', label: 'Awaiting decision', test: b => b.status === 'Awaiting decision' },
  { value: 'nonext', label: 'No next action', test: b => OPEN_STATUSES.includes(b.status) && !String(b.next_action || '').trim() },
  { value: 'decided', label: 'Decided', test: b => OUTCOME_STATUSES.includes(b.status) },
  { value: 'all', label: 'All', test: () => true },
  { value: 'due14', label: 'Due within 14 days', chip: false, test: b => { const n = daysTo(b.deadline); return PRE_SUBMISSION.includes(b.status) && n != null && n >= 0 && n <= 14; } },
  { value: 'waiting', label: 'Waiting on a buyer', chip: false, test: b => ['Responded', 'Awaiting decision'].includes(b.status) },
];

function BidsTab(props) {
  return <BidRows {...props} rows={props.bids} showQuick empty="No bids match this filter." />;
}

// ============ Leads tab ============
// Referrals, enquiries and MSP conversations that are not formal tenders
// (migration 0417): a lead is a bid row of kind 'lead'. Its clock is the next
// action's due date, not a deadline, so the list is ordered by follow-up. The
// value total is the leads' own, kept out of the tender pipeline on purpose.
const FOLLOW_TONES = {
  red: 'bg-red-50 text-red-700',
  orange: 'bg-amber-50 text-amber-700',
};
export function FollowUpBadge({ b }) {
  const f = followUpState(b);
  if (!f || f.key === 'later') return null;
  return <span className={`px-1.5 py-px rounded-full text-[10px] font-bold whitespace-nowrap ${FOLLOW_TONES[f.tone] || 'bg-gray-100 text-gray-600'}`}>{f.label}</span>;
}

function LeadsTab({ leads, people, currentUser, view, onNew }) {
  const { navigate } = useApp();
  const [quick, setQuick] = useState('open');
  const [mineOnly, setMineOnly] = useState(false);
  const [text, setText] = useState('');
  const pMap = new Map((people || []).map(p => [p.id, `${p.first_name || ''} ${p.last_name || ''}`.trim()]));
  const k = leadKpis(leads, currentUser?.id, new Date());
  const value = k.value.value == null ? '—' : maskMoney(fmtBoardValue(k.value.value), !!view.hideValues);
  const q = LEAD_QUICK.find(x => x.value === quick) || LEAD_QUICK[0];
  const needle = text.trim().toLowerCase();
  const rows = sortLeads((leads || []).filter(b => !b.merged_into_id && q.test(b, new Date())
    && (!mineOnly || (currentUser && b.owner_id === currentUser.id))
    && (!needle || [b.reference, b.opportunity, b.client_name, b.buyer, b.source, b.summary, b.next_action].some(v => String(v || '').toLowerCase().includes(needle)))));
  const chip = (on) => `px-2.5 py-1 rounded-full border text-[11.5px] font-semibold ${on ? 'bg-navy text-white border-navy' : 'bg-white text-gray-700 border-gray-200 hover:border-gold'}`;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 10, marginBottom: 14 }} data-tour="lead-kpis">
        {kpi(k.open, 'Open leads', null, { sub: 'not formal tenders', onClick: () => { setQuick('open'); setMineOnly(false); } })}
        {kpi(k.mine, 'Yours', null, { sub: 'open leads you own', onClick: () => { setQuick('open'); setMineOnly(true); } })}
        {kpi(k.followUpDue, 'Follow-up due', '#EF4444', { sub: 'overdue, today or none set', onClick: () => setQuick('followup') })}
        {kpi(k.inDiscussion, 'In discussion', '#0891B2', { sub: 'talking terms' })}
        {kpi(value, 'Lead value ex VAT', null, { sub: 'kept out of the tender pipeline' })}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-2.5" data-tour="lead-filters">
        {LEAD_QUICK.map(x => (
          <button key={x.value} type="button" onClick={() => setQuick(x.value)} aria-pressed={quick === x.value} className={chip(quick === x.value)}>{x.label}</button>
        ))}
        <label className="flex items-center gap-1.5 text-[12px] text-gray-600 ml-1 cursor-pointer">
          <input type="checkbox" checked={mineOnly} onChange={e => setMineOnly(e.target.checked)} /> Mine only
        </label>
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Search leads" aria-label="Search leads"
          className="form-input text-[12px] ml-auto" style={{ width: 'auto', minWidth: 160, maxWidth: '100%' }} />
      </div>
      <div className="bg-white border border-gray-200 rounded-xl" data-tour="lead-list">
        {rows.length === 0 ? (
          <div className="p-5 text-center text-[12px] text-gray-500">
            {(leads || []).length === 0
              ? <>No leads yet. A lead is a conversation that is not a formal tender: a referral, an enquiry, an MSP renewal. <button type="button" onClick={onNew} className="font-semibold text-navy underline">Raise the first one</button>.</>
              : 'No leads match this filter.'}
          </div>
        ) : rows.map(b => {
          const owner = (b.owner_id && pMap.get(b.owner_id)) || b.owner || '';
          return (
            <div key={b.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3.5 gap-y-1 px-3.5 py-2.5 border-t border-gray-100 first:border-t-0">
              <div className="min-w-0">
                <div className="text-[12px]">
                  <span className="font-mono text-[11px] font-bold text-gold mr-1.5">{b.reference || `#${b.id}`}</span>
                  <button type="button" onClick={() => navigate('bid-detail', b.id)} className="font-bold text-gray-900 hover:text-gold text-left">{b.opportunity}</button>
                </div>
                <div className="text-[11px] text-gray-500">{[clientNameOf(b) || 'No client', b.source, owner, b.last_touch_at ? `touched ${fmtTouch(b.last_touch_at)}` : null].filter(Boolean).join(' · ')}</div>
                <div className="mt-0.5"><NextLine b={b} people={pMap} /></div>
              </div>
              <div className="flex flex-col items-end gap-1 text-right">
                <div className="text-[12px]"><ValueCell b={b} size={12} /></div>
                <div className="flex flex-wrap justify-end items-center gap-1">
                  <Pill text={b.status || 'Upcoming'} palette={STATUS_COLORS} />
                  <FollowUpBadge b={b} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Shared by My bids (rows already narrowed to mine) and Bids. Filters, sort
// and the chosen fields come from the saved view, so they follow the person.
function BidRows({ rows: input, columns, people, clients, isAdmin, reload, setColumns, showToast, view, updateView, showQuick, title, empty }) {
  const { navigate } = useApp();
  const pMap = new Map((people || []).map(p => [p.id, `${p.first_name || ''} ${p.last_name || ''}`.trim()]));
  const f = view.filters || {};
  const setF = (patch) => updateView(v => ({ ...v, filters: { ...v.filters, ...patch } }));
  const quick = QUICK.find(q => q.value === (f.quick || 'open')) || QUICK[0];
  const [expanded, setExpanded] = useState(null);
  const [editBid, setEditBid] = useState(null);
  const [outcomeBid, setOutcomeBid] = useState(null);
  const [showFields, setShowFields] = useState(false);
  const sortDir = f.sort || null;   // null is attention order
  const cycleSort = () => setF({ sort: sortDir === null ? 'asc' : sortDir === 'asc' ? 'desc' : null });
  const fields = initialFields(view, columns);
  const labels = new Map([...ROW_FIELDS.map(x => [x.key, x.label]), ...columns.map(c => ['custom:' + c.key, c.label])]);
  const sectors = [...new Set(input.map(b => b.sector).filter(Boolean))].sort();
  const text = String(f.text || '').trim().toLowerCase();

  const filtered = input
    .filter(b => !showQuick || quick.test(b))
    .filter(b => !f.status || b.status === f.status)
    .filter(b => !f.sector || b.sector === f.sector)
    .filter(b => !text || [b.reference, b.opportunity, clientNameOf(b)].some(v => String(v || '').toLowerCase().includes(text)))
    .sort((x, y) => (sortDir ? deadlineSort(x, y, sortDir) : (attentionRank(x) - attentionRank(y) || deadlineSort(x, y))));
  const totals = valueTotals(filtered);
  const hide = !!view.hideValues;

  const deleteBid = async (b) => {
    if (!confirm(`Delete "${b.opportunity}"? This cannot be undone.`)) return;
    try { await api.delete('crm/bids/' + b.id); showToast('Deleted'); reload(); } catch (e) { showToast(e.message, 'error'); }
  };
  const chipClass = (on) => `px-2 py-0.5 rounded-full border text-[11px] font-semibold ${on ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`;
  const btn = 'px-2.5 py-1 text-[11px] font-semibold rounded-md border border-gray-200 bg-white text-gray-700 hover:border-gold';

  return (
    <div>
      {title && <div className="text-[13px] font-bold text-gray-900 mb-2">{title}</div>}
      {showQuick && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-tour="quick-filters">
          {QUICK.filter(q => q.chip !== false).map(q => (
            <button key={q.value} type="button" onClick={() => setF({ quick: q.value, status: '' })} aria-pressed={quick.value === q.value && !f.status} className={chipClass(quick.value === q.value && !f.status)}>
              {q.label}{q.value !== 'all' && <span className="ml-1 opacity-60">{input.filter(q.test).length}</span>}
            </button>
          ))}
          {quick.chip === false && (
            <button type="button" onClick={() => setF({ quick: 'open' })} className="px-2 py-0.5 rounded-full border text-[11px] font-semibold bg-gold/10 text-gold border-gold" title="Clear this filter">
              {quick.label} ✕
            </button>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <input value={f.text || ''} onChange={e => setF({ text: e.target.value })} placeholder="Filter by reference, title or buyer" className="form-input text-[12px]" style={{ width: 'auto', flex: '1 1 200px', minWidth: 0 }} />
        {showQuick && (
          <select value={f.status || ''} onChange={e => setF({ status: e.target.value, quick: e.target.value ? 'all' : (f.quick || 'open') })} className="form-select text-[12px]" style={{ width: 'auto', flex: '0 1 auto' }}>
            <option value="">All statuses</option>
            {STATUSES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
        {sectors.length > 0 && (
          <select value={f.sector || ''} onChange={e => setF({ sector: e.target.value })} className="form-select text-[12px]" style={{ width: 'auto', flex: '0 1 auto' }}>
            <option value="">All sectors</option>
            {sectors.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <button type="button" onClick={cycleSort} className={btn}
          title={sortDir === null ? 'Attention order. Click to sort by deadline, soonest first' : sortDir === 'asc' ? 'Soonest first. Click for latest first' : 'Latest first. Click to return to attention order'}>
          Deadline {sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '⇅'}
        </button>
        <button type="button" onClick={() => setShowFields(true)} className={btn} data-tour="fields" title="Choose the fields shown under each bid. Saved to your login.">
          Fields ({fields.length})
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {filtered.length === 0
          ? <div className="p-5 text-center text-[11px] text-gray-400">{empty || 'No bids match this filter.'}</div>
          : filtered.map(b => (
            <BidRow key={b.id} b={b} fields={fields} labels={labels} people={pMap} ownerName={ownerNameOf(b, pMap)} isAdmin={isAdmin}
              expanded={expanded === b.id} onToggle={() => setExpanded(expanded === b.id ? null : b.id)}
              onOpen={() => navigate('bid-detail', b.id)} onEdit={() => setEditBid(b)} onOutcome={() => setOutcomeBid(b)} onDelete={() => deleteBid(b)} />
          ))}
        <div className="px-3 py-2 border-t border-gray-200 text-[11px] text-gray-500 flex flex-wrap justify-between gap-x-3 gap-y-1">
          <span>{filtered.length} bid{filtered.length === 1 ? '' : 's'}</span>
          <span>
            Total ex VAT (contract values only): <b className="text-gray-800">{totals.value == null ? '—' : maskMoney(formatValueGbp(totals.value), hide)}</b>
            {totals.ceilings > 0 ? ` · ${totals.ceilings} framework ceiling${totals.ceilings === 1 ? '' : 's'} excluded` : ''}
            {totals.unpriced > 0 ? ` · ${totals.unpriced} unpriced` : ''}
          </span>
        </div>
      </div>

      {editBid && (
        <BidForm existing={editBid} columns={columns} people={people} clients={clients} onClose={() => setEditBid(null)}
          onSaved={() => { setEditBid(null); reload(); showToast('Saved'); }} onError={m => showToast(m, 'error')} />
      )}
      {outcomeBid && (
        <OutcomeForm b={outcomeBid} onClose={() => setOutcomeBid(null)} onSaved={(res) => { setOutcomeBid(null); reload(); outcomeToast(showToast, res); }} onError={m => showToast(m, 'error')} />
      )}
      {showFields && (
        <FieldsManager fields={fields} columns={columns} isAdmin={isAdmin} updateView={updateView}
          reloadColumns={async () => { const d = await api.get('crm/columns').catch(() => ({ rows: [] })); setColumns(d?.rows || []); }}
          onClose={() => setShowFields(false)} showToast={showToast} />
      )}
    </div>
  );
}

// One bid: what it is and what is next on the left, the value, deadline and
// pills on the right, the chosen fields as chips underneath. A two-column
// grid that wraps, so it never needs to scroll sideways.
function BidRow({ b, fields, labels, people, ownerName, isAdmin, expanded, onToggle, onOpen, onEdit, onOutcome, onDelete }) {
  const checked = b.last_checked_at ? `checked ${fmtTouch(b.last_checked_at)}` : 'never checked';
  const when = b.deadline ? fmtDeadlineWithTime(b.deadline, b.deadline_time) : b.watch_date ? `Watch ${fmtDeadline(b.watch_date)}` : 'No deadline';
  const btn = 'px-2.5 py-1 text-[11px] font-semibold rounded-md border border-gray-200 bg-white text-gray-700 hover:border-gold';
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3.5 gap-y-1 px-3.5 py-2.5 border-t border-gray-100 first:border-t-0">
      <div className="min-w-0">
        <div className="text-[12px]">
          <span className="font-mono text-[11px] font-bold text-gold mr-1.5">{b.reference || `#${b.id}`}</span>
          <button type="button" onClick={onOpen} className="font-bold text-gray-900 hover:text-gold text-left">{b.opportunity}</button>
        </div>
        <div className="text-[11px] text-gray-500">{[clientNameOf(b) || 'No client', b.sector, ownerName, checked].filter(Boolean).join(' · ')}</div>
        <div className="mt-0.5"><NextLine b={b} people={people} /></div>
      </div>
      <div className="flex flex-col items-end gap-1 text-right">
        <div className="text-[12px]"><ValueCell b={b} size={12} /></div>
        <div className="text-[11px] text-gray-500 whitespace-nowrap">{when}</div>
        <div className="flex flex-wrap justify-end items-center gap-1">
          <Pill text={b.status || 'Evaluating'} palette={STATUS_COLORS} />
          <UrgencyBadge b={b} />
          <GoneQuietBadge b={b} />
          <LongWaitBadge b={b} />
          <button type="button" onClick={onToggle} aria-expanded={expanded} title="Actions for this bid" className="text-[12px] text-gray-400 hover:text-navy px-1">{expanded ? '▴' : '▾'}</button>
        </div>
      </div>
      {fields.length > 0 && (
        <div className="col-span-2 flex flex-wrap gap-1">
          {fields.map(k => {
            const v = fieldValue(b, k, { people });
            return <span key={k} className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-1.5 py-px">{labels.get(k) || k}: <b className="text-gray-800 font-semibold">{v ?? '—'}</b></span>;
          })}
        </div>
      )}
      {expanded && (
        <div className="col-span-2 bg-gray-50 rounded p-2 flex flex-wrap items-center gap-1.5">
          <button type="button" onClick={onOpen} className={btn}>Open bid →</button>
          <button type="button" onClick={onEdit} className={btn}>Edit</button>
          <button type="button" onClick={onOutcome} className={btn}>Record outcome</button>
          {isAdmin && <button type="button" onClick={onDelete} className={`${btn} text-red-600 border-red-200`}>Delete</button>}
          <span className="basis-full text-[11px] text-gray-500 whitespace-pre-wrap">{b.summary || 'No summary yet.'}</span>
        </div>
      )}
    </div>
  );
}

// ============ Review tab ============
// The weekly review, computed from ops (decision 13 of the redesign): what
// needs attention, what changed status this week, your bid actions by date,
// and the bids checked this week with nothing to report. Nothing here comes
// from outside ops.
function ReviewTab({ bids, currentUser, people }) {
  const { navigate } = useApp();
  const [mine, setMine] = useState(true);
  const [report, setReport] = useState(null);
  const [acts, setActs] = useState([]);
  const since = weekAgo(new Date());
  const loadActs = () => api.get('actions/mine')
    .then(r => setActs((Array.isArray(r) ? r : []).filter(a => a.entity_type === 'bid')))
    .catch(() => setActs([]));
  useEffect(() => {
    api.get('crm/bids/changes', { from: since, to: todayIso() }).then(setReport).catch(() => setReport({ rows: [] }));
    loadActs();
  }, []);

  const pMap = new Map((people || []).map(p => [p.id, `${p.first_name || ''} ${p.last_name || ''}`.trim()]));
  const mineId = mine ? currentUser?.id : null;
  const inScope = (b) => !mineId || b.owner_id === mineId;
  const byId = new Map(bids.map(b => [String(b.id), b]));
  const attention = bids.filter(b => needsAttention(b) && inScope(b)).sort((x, y) => attentionRank(x) - attentionRank(y) || deadlineSort(x, y));
  const changes = statusChangesThisWeek(report, { mineId });
  const quietChecks = checkedWithNoChange(bids, new Set(changes.map(c => c.bid_id)), since, mineId);
  const counts = OPEN_STATUSES.map(s => [s, bids.filter(b => b.status === s && inScope(b)).length]).filter(([, n]) => n > 0);
  const groups = new Map();
  for (const a of [...acts].sort((x, y) => String(x.due_date || '9999').localeCompare(String(y.due_date || '9999')))) {
    const k = a.due_date ? String(a.due_date).slice(0, 10) : '';
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(a);
  }
  const reasons = (b) => {
    const a = b.attention || {};
    return [
      a.due_now && 'Due within 3 days',
      a.due_soon && `Due in ${a.due_in} days`,
      a.no_next_action && 'No next action',
      a.chase_due && 'Chase the buyer',
      a.portal_due && (b.last_checked_at ? `Portal not checked since ${fmtTouch(b.last_checked_at)}` : 'Portal never checked'),
      a.long_wait && 'Submitted 6+ months ago: close it?',
      a.follow_up_due && 'Lead follow-up overdue',
    ].filter(Boolean).join(' · ');
  };
  const card = 'bg-white border border-gray-200 rounded-lg p-3';
  const h = 'text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5';
  const open = (id) => navigate('bid-detail', id);

  return (
    <div>
      <div className="rounded-lg px-3.5 py-3 mb-3" style={{ background: 'var(--navy)', color: '#E2E8F0' }} data-tour="review-banner">
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px]">
          <span><b style={{ color: '#E5B87A' }}>AS OF</b> {fmtDeadline(todayIso())}</span>
          <span><b style={{ color: '#E5B87A' }}>THIS WEEK</b> since {fmtDeadline(since)}</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {counts.map(([s, n]) => (
            <span key={s} className="inline-flex items-center gap-1"><Pill text={s} palette={STATUS_COLORS} /><span className="text-[11px] font-bold">{n}</span></span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex border border-gray-200 rounded-md overflow-hidden">
          <button type="button" onClick={() => setMine(true)} aria-pressed={mine} className={`px-2.5 py-1 text-[11px] font-semibold ${mine ? 'bg-navy text-white' : 'bg-white text-gray-600'}`}>Mine</button>
          <button type="button" onClick={() => setMine(false)} aria-pressed={!mine} className={`px-2.5 py-1 text-[11px] font-semibold ${!mine ? 'bg-navy text-white' : 'bg-white text-gray-600'}`}>Everyone</button>
        </div>
        <span className="text-[11px] text-gray-500">{attention.length} bid{attention.length === 1 ? '' : 's'} need attention. {mine ? 'The same count is on the sidebar badge.' : ''}</span>
      </div>

      <div className="grid gap-3 items-start" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 330px), 1fr))' }}>
        <div className="space-y-3 min-w-0">
          <div className={card} data-tour="review-attention">
            <div className={h}>Needs attention</div>
            {attention.length === 0 ? <div className="text-[11px] text-gray-400">Nothing needs attention.</div> : attention.map(b => (
              <div key={b.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 py-1.5 border-t border-gray-100 first:border-t-0">
                <div className="min-w-0">
                  <span className="font-mono text-[11px] font-bold text-gold mr-1.5">{b.reference || `#${b.id}`}</span>
                  <button type="button" onClick={() => open(b.id)} className="text-[12px] font-bold text-gray-900 hover:text-gold text-left">{b.opportunity}</button>
                  <div className="text-[11px] text-gray-500">{reasons(b)}{mine ? '' : ` · ${ownerNameOf(b, pMap) || 'no owner'}`}</div>
                </div>
                <div className="flex items-start gap-1"><UrgencyBadge b={b} /><GoneQuietBadge b={b} /><LongWaitBadge b={b} /></div>
              </div>
            ))}
          </div>

          <div className={card}>
            <div className={h}>Changed status this week</div>
            {report == null ? <div className="text-[11px] text-gray-400">Loading…</div>
              : changes.length === 0 ? <div className="text-[11px] text-gray-400">No status changes this week.</div>
              : changes.map((c, i) => (
                <div key={`${c.bid_id}-${i}`} className="grid grid-cols-[auto_minmax(0,1fr)] gap-2 py-1.5 border-t border-gray-100 first:border-t-0">
                  <div><Pill text={c.to_status} palette={STATUS_COLORS} /></div>
                  <div className="min-w-0">
                    <span className="font-mono text-[11px] font-bold text-gold mr-1.5">{c.reference || `#${c.bid_id}`}</span>
                    <button type="button" onClick={() => open(c.bid_id)} className="text-[12px] font-bold text-gray-900 hover:text-gold text-left">{c.opportunity}</button>
                    <div className="text-[11px] text-gray-500">{c.from_status ? `${c.from_status} → ${c.to_status}` : `Raised in ${c.to_status}`} · {fmtTouch(c.changed_at)}{c.changed_by_name ? ` · ${c.changed_by_name}` : ''}</div>
                  </div>
                </div>
              ))}
          </div>

          <div className={card} data-tour="review-actions">
            <div className={h}>Your actions, by date</div>
            {acts.length === 0 ? <div className="text-[11px] text-gray-400">No open actions on your bids.</div> : [...groups.entries()].map(([day, list]) => (
              <div key={day || 'none'} className="mb-2 last:mb-0">
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-1">{day ? fmtDeadline(day) : 'No date'}{day && day < todayIso() ? ' · overdue' : day === todayIso() ? ' · today' : ''}</div>
                {list.map(a => <ReviewAction key={a.id} a={a} bid={byId.get(String(a.entity_id))} onDone={loadActs} onOpen={open} />)}
              </div>
            ))}
            <div className="text-[10px] text-gray-400 mt-1.5">The same actions as the Actions tab on each bid.</div>
          </div>
        </div>

        <div className="space-y-3 min-w-0">
          <div className={card}>
            <div className={h}>Checked with no change</div>
            {quietChecks.length === 0 ? <div className="text-[11px] text-gray-400">No portal checks this week.</div> : quietChecks.map(b => (
              <div key={b.id} className="flex justify-between gap-2 py-1 border-t border-gray-100 first:border-t-0 text-[11px]">
                <span className="min-w-0"><span className="font-mono font-bold text-gold mr-1.5">{b.reference || `#${b.id}`}</span>
                  <button type="button" onClick={() => open(b.id)} className="text-gray-800 hover:text-gold text-left">{b.opportunity}</button></span>
                <span className="text-gray-500 whitespace-nowrap">{fmtTouch(b.last_checked_at)}</span>
              </div>
            ))}
            <div className="text-[10px] text-gray-400 mt-1.5">Portal checked this week, with no status change.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// An action on Review. Ticking it asks what was done, since the Actions page
// needs an update note to complete one, and completes it where it is.
function ReviewAction({ a, bid, onDone, onOpen }) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const complete = async () => {
    if (!note.trim()) { setErr('Say what was done.'); return; }
    setBusy(true); setErr('');
    try { await api.post(`actions/${a.id}/complete`, { note: note.trim() }); onDone(); }
    catch (e) { setErr(e?.message || 'Could not complete this action.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-[16px_minmax(0,1fr)_auto] gap-2 py-1.5 border-t border-gray-100 first:border-t-0 items-start">
      <button type="button" onClick={() => setAsking(v => !v)} aria-label="Mark this action done" title="Mark done"
        className="w-3.5 h-3.5 mt-0.5 rounded border border-gray-300 bg-white hover:border-gold" />
      <div className="min-w-0">
        <div className="text-[12px] font-semibold text-gray-800">{a.title}</div>
        {a.last_message && <div className="text-[11px] text-gray-500 truncate">{a.last_message}</div>}
        {asking && (
          <div className="mt-1 flex flex-wrap gap-1.5 items-center">
            <input value={note} onChange={e => { setNote(e.target.value); setErr(''); }} placeholder="What was done?" className="form-input text-[12px]" style={{ width: 'auto', flex: '1 1 180px', minWidth: 0 }} autoFocus />
            <button type="button" onClick={complete} disabled={busy} className="px-2.5 py-1 text-[11px] font-semibold rounded-md bg-navy text-white disabled:opacity-50">{busy ? 'Saving…' : 'Complete'}</button>
            {err && <span role="alert" className="basis-full text-[11px] text-red-600">{err}</span>}
          </div>
        )}
      </div>
      {bid ? <button type="button" onClick={() => onOpen(bid.id)} className="text-[11px] font-bold text-gold hover:underline whitespace-nowrap">{bid.reference || `#${bid.id}`} →</button> : <span />}
    </div>
  );
}

// ============ Buyers tab ============
// A buyer is a client with bids (build step 2 of the redesign): the list on
// the left, the chosen one on the right with the bid team's key fields and
// intelligence, its bids and its contacts. Bids naming a buyer with no client
// linked are grouped by that name and say how to link them.
const rowsOf = (d) => (Array.isArray(d) ? d : (d?.rows || []));

// Organisations (the buyers) and People (their contacts, formerly the
// Contacts tab) are one tab with two views: both are who we bid to.
function BuyersTab(props) {
  const [show, setShow] = useState('organisations');
  const seg = (on) => `px-3 py-1 text-[11.5px] font-semibold ${on ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`;
  return (
    <div>
      <div className="inline-flex border border-gray-200 rounded-lg overflow-hidden mb-3" data-tour="buyers-views">
        <button type="button" onClick={() => setShow('organisations')} aria-pressed={show === 'organisations'} className={seg(show === 'organisations')}>Organisations</button>
        <button type="button" onClick={() => setShow('people')} aria-pressed={show === 'people'} className={seg(show === 'people')} data-tour="buyers-people">People</button>
      </div>
      {show === 'organisations' ? <BuyerOrganisations {...props} /> : <ContactsTab />}
    </div>
  );
}

function BuyerOrganisations({ bids, clients, setClients, view, showToast }) {
  const { navigate } = useApp();
  const list = buyerList(bids, clients);
  const [selKey, setSelKey] = useState(null);
  const [contacts, setContacts] = useState([]);
  useEffect(() => { api.get('crm/contacts').then(d => setContacts(rowsOf(d))).catch(() => setContacts([])); }, []);
  const sel = list.find(b => b.key === selKey) || list[0] || null;
  const hide = !!view.hideValues;
  const value = (t) => (t.value == null ? '—' : maskMoney(fmtBoardValue(t.value), hide));
  const card = 'bg-white border border-gray-200 rounded-lg p-3';
  const h = 'text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5';

  if (!list.length) return <div className="p-6 text-[12px] text-gray-400">No bids name a buyer yet.</div>;

  return (
    <div className="grid gap-3 items-start" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))' }}>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden min-w-0" data-tour="buyers-list">
        {list.map(b => (
          <button key={b.key} type="button" onClick={() => setSelKey(b.key)} aria-pressed={sel && sel.key === b.key}
            className={`block w-full text-left px-3 py-2 border-t border-gray-100 first:border-t-0 border-l-4 ${sel && sel.key === b.key ? 'border-l-gold bg-gray-50' : 'border-l-transparent hover:bg-gray-50'}`}>
            <div className="text-[12.5px] font-bold text-gray-900">{b.name}{b.unlinked && <span className="ml-1.5 text-[10px] font-bold text-amber-700 uppercase">unlinked</span>}</div>
            <div className="text-[11px] text-gray-500">{[b.sector, `${b.bids.length} bid${b.bids.length === 1 ? '' : 's'}`, value(b.totals), `W${b.won} L${b.lost}`].filter(Boolean).join(' · ')}</div>
          </button>
        ))}
      </div>

      {sel && (
        <div className="space-y-3 min-w-0">
          <div className={card}>
            <div className="text-[15px] font-extrabold text-navy">{sel.name}</div>
            <div className="flex flex-wrap gap-1.5 mt-1.5 text-[11px]">
              <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-bold">{sel.bids.length} bid{sel.bids.length === 1 ? '' : 's'}</span>
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold">{sel.won} won</span>
              <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-bold">{sel.lost} lost</span>
              <span className="px-2 py-0.5 rounded-full bg-gold/10 text-gold font-bold">{value(sel.totals)} ex VAT{sel.totals.ceilings ? ` +${sel.totals.ceilings} ceiling` : ''}</span>
              {sel.sector && <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-bold">{sel.sector}</span>}
            </div>
            {sel.unlinked ? (
              <div className="mt-2 text-[11.5px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-2.5 py-1.5">
                These bids name a buyer that is not linked to a client, so there are no key fields to keep. Link each bid to its client from the bid's Edit form, or add the client under Clients first.
              </div>
            ) : (
              <BuyerFields client={sel.client} showToast={showToast}
                onSaved={(row) => setClients(list => list.map(c => (c.id === row.id ? { ...c, ...row } : c)))} />
            )}
          </div>

          <div className={card}>
            <div className={h}>Bids with this buyer</div>
            {sel.bids.map(b => (
              <div key={b.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 py-1.5 border-t border-gray-100 first:border-t-0 items-center">
                <div className="min-w-0 text-[12px]">
                  <span className="font-mono text-[11px] font-bold text-gold mr-1.5">{b.reference || `#${b.id}`}</span>
                  <button type="button" onClick={() => navigate('bid-detail', b.id)} className="font-semibold text-gray-900 hover:text-gold text-left">{b.opportunity}</button>
                </div>
                <div className="flex items-center gap-1.5 text-[11.5px]"><ValueCell b={b} /><Pill text={b.status} palette={STATUS_COLORS} /></div>
              </div>
            ))}
          </div>

          <div className={card}>
            <div className={h}>Contacts</div>
            {(() => {
              const people = sel.clientId ? contacts.filter(c => c.client_id === sel.clientId) : [];
              if (!people.length) return <div className="text-[11px] text-gray-400">None recorded. <button type="button" onClick={() => navigate('crm-contacts')} className="font-semibold text-navy hover:underline">Add them in CRM Contacts</button></div>;
              return people.map(c => (
                <div key={c.id} className="flex justify-between gap-2 py-1 border-t border-gray-100 first:border-t-0 text-[11.5px]">
                  <span className="min-w-0"><b className="text-gray-800">{`${c.first_name || ''} ${c.last_name || ''}`.trim()}</b>{c.job_title || c.role ? <span className="text-gray-500">, {c.job_title || c.role}</span> : null}</span>
                  {c.email && <a href={`mailto:${c.email}`} className="text-blue-700 hover:underline truncate">{c.email}</a>}
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// The bid team's key fields on a buyer, edited in place. The address belongs
// to the client record and is shown as it is.
const BUYER_FIELD_LABELS = [
  ['org_number', 'Organisation number', 'Companies House or other organisation number'],
  ['bid_portal', 'Portal', 'Where it usually tenders, such as ProContract'],
  ['typical_route', 'Typical route', 'Open procedure, a framework call-off, RFQ under threshold…'],
];
function BuyerFields({ client, showToast, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({});
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setEditing(false); setErr(''); }, [client.id]);
  const start = () => { setF({ org_number: client.org_number || '', bid_portal: client.bid_portal || '', typical_route: client.typical_route || '', intelligence_note: client.intelligence_note || '' }); setEditing(true); };
  const save = async () => {
    setSaving(true); setErr('');
    try { const row = await api.put(`crm/buyers/${client.id}`, f); onSaved(row); setEditing(false); showToast('Buyer saved'); }
    catch (e) { setErr(e?.message || 'Could not save the buyer.'); }
    finally { setSaving(false); }
  };
  const row = (label, value) => (
    <div className="flex justify-between gap-3 py-1 border-t border-gray-100 text-[11.5px]">
      <span className="text-gray-500">{label}</span><span className="text-right text-gray-800 min-w-0">{value || <span className="text-gray-400">—</span>}</span>
    </div>
  );
  if (editing) {
    return (
      <div className="mt-2 space-y-1.5">
        {BUYER_FIELD_LABELS.map(([k, label, hint]) => (
          <label key={k} className="block text-[10px] font-bold text-gray-500 uppercase">{label}
            <input value={f[k]} onChange={e => setF(p => ({ ...p, [k]: e.target.value }))} placeholder={hint} className="form-input text-[12px] mt-0.5 normal-case font-normal" />
          </label>
        ))}
        <label className="block text-[10px] font-bold text-gray-500 uppercase">Intelligence
          <textarea value={f.intelligence_note} onChange={e => setF(p => ({ ...p, intelligence_note: e.target.value }))} rows={4} placeholder="What we know about how this buyer decides, and what has worked" className="form-input text-[12px] mt-0.5 normal-case font-normal" />
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={save} disabled={saving} className="px-2.5 py-1 bg-navy text-white text-[12px] font-semibold rounded disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" onClick={() => setEditing(false)} className="px-2 py-1 text-[11px] font-semibold border border-gray-200 text-gray-600 rounded">Cancel</button>
          {err && <span role="alert" className="text-[11px] text-red-600">{err}</span>}
        </div>
      </div>
    );
  }
  return (
    <div className="mt-2">
      {BUYER_FIELD_LABELS.map(([k, label]) => <div key={k}>{row(label, client[k])}</div>)}
      {row('Address', client.address)}
      <div className="pt-1.5 border-t border-gray-100">
        <div className="flex justify-between items-center">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Intelligence</span>
          <button type="button" onClick={start} className="text-[11px] font-semibold text-navy hover:underline">Edit</button>
        </div>
        <div className="text-[12px] text-gray-700 whitespace-pre-wrap mt-0.5">{client.intelligence_note || <span className="text-gray-400">Nothing recorded yet.</span>}</div>
        {client.intelligence_updated_at && <div className="text-[10px] text-gray-400 mt-0.5">Updated {fmtTouch(client.intelligence_updated_at)}</div>}
      </div>
    </div>
  );
}

// ============ Buyers: People ============
// The people at our buyers (the Contacts tab until 25 October 2026), from the one contacts table (client_contacts,
// the same as the CRM Contacts page, where they are added and edited), with
// the roles they have held on bids and a warning when a referee is being
// leaned on.
const ROLE_COLORS = {
  'Procurement':    { bg: '#EFF6FF', color: '#1D4ED8' },
  'Referee':        { bg: '#FBF3E8', color: '#A1723D' },
  'Portal contact': { bg: '#F3E8FF', color: '#7E22CE' },
  'Decision maker': { bg: '#F0FDFA', color: '#0F766E' },
};

function ContactsTab() {
  const { navigate } = useApp();
  const [contacts, setContacts] = useState(null);
  const [roles, setRoles] = useState([]);
  const [text, setText] = useState('');
  useEffect(() => {
    api.get('crm/contacts').then(d => setContacts(rowsOf(d))).catch(() => setContacts([]));
    api.get('crm/bids/contact-roles').then(d => setRoles(d?.rows || [])).catch(() => setRoles([]));
  }, []);
  const q = text.trim().toLowerCase();
  const cards = contactCards(contacts || [], roles)
    .filter(c => !q || [c.name, c.organisation, c.title, c.email].some(v => String(v || '').toLowerCase().includes(q)))
    .sort((a, b) => b.bids.length - a.bids.length || a.name.localeCompare(b.name));
  const byId = new Map(contactCards(contacts || [], roles).map(c => [c.id, c]));
  const load = refereeLoad(roles, new Date()).filter(l => byId.has(l.contact_id));

  // The tour's anchor is on the whole view, which always renders, rather than
  // on the grid, which is absent while loading or with no contacts.
  return (
    <div data-tour="contacts-grid">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Filter by name, organisation or email" className="form-input text-[12px]" style={{ width: 'auto', flex: '1 1 220px', minWidth: 0 }} />
        <button type="button" onClick={() => navigate('crm-contacts')} className="px-2.5 py-1 text-[11px] font-semibold rounded-md border border-gray-200 bg-white text-gray-700 hover:border-gold">Add or edit in CRM Contacts →</button>
      </div>
      {contacts == null ? <div className="text-[12px] text-gray-400">Loading…</div> : cards.length === 0 ? <div className="text-[12px] text-gray-400">No contacts match.</div> : (
        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))' }}>
          {cards.map(c => (
            <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-3 flex gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-full bg-gray-100 text-gold font-extrabold text-[13px] flex items-center justify-center flex-shrink-0">{c.initials || '?'}</div>
              <div className="min-w-0 flex-1">
                <div className="flex justify-between gap-1.5 flex-wrap">
                  <b className="text-[12.5px] text-gray-900">{c.name || 'Unnamed'}</b>
                  <span className="flex flex-wrap gap-1">{c.roles.map(r => <Pill key={r} text={r} palette={ROLE_COLORS} />)}</span>
                </div>
                <div className="text-[11px] text-gray-500">{[c.title, c.organisation].filter(Boolean).join(' · ') || 'No title or organisation'}</div>
                <div className="text-[11.5px] mt-1 flex flex-wrap gap-x-2">
                  {c.email && <a href={`mailto:${c.email}`} className="text-blue-700 hover:underline break-all">{c.email}</a>}
                  {c.phone && <a href={`tel:${c.phone}`} className="text-gray-700">{c.phone}</a>}
                </div>
                <div className="text-[10.5px] text-gray-400 mt-0.5">{c.lastContact ? `Last contact ${fmtDeadline(c.lastContact)}` : 'No contact logged'}</div>
                {c.bids.length > 0 && (
                  <div className="flex flex-wrap gap-x-1.5 mt-1">
                    {c.bids.map(b => (
                      <button key={b.id} type="button" onClick={() => navigate('bid-detail', b.id)} title={`${b.opportunity}${b.role ? ` · ${b.role}` : ''}`}
                        className="font-mono text-[11px] font-bold text-gold hover:underline">{b.reference || `#${b.id}`}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {load.length > 0 && (
        <div role="status" className="mt-3 text-[11.5px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
          <b>Referee load.</b> {load.map(l => `${byId.get(l.contact_id).name} has been a referee on ${l.count} bids in the last 12 months`).join('; ')}. A buyer that hears from the same referee on every submission gives the reference less weight; consider asking someone else next time.
        </div>
      )}
    </div>
  );
}

// ============ Report tab ============
// A month of bid activity, for the management report: what went in, what was
// decided, what is still open, who did what, and the win rate by sector and
// service line. GET crm/bids/report does the counting from bid_status_history
// (so a bid that went in and was won in the same month appears in both) and
// the sheets in utils/bidReportExport.js shape the answer. The tables here and
// the workbook Export to Excel writes are drawn from those same sheets, so the
// screen and the file always agree.
function ReportTab({ bids = [] }) {
  const isDark = useDarkMode();
  const { navigate } = useApp();
  const [range, setRange] = useState(() => previousCalendarMonth());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [exportErr, setExportErr] = useState('');

  useEffect(() => {
    if (!range.from || !range.to) return undefined;
    let alive = true;
    setLoading(true); setErr('');
    api.get('crm/bids/report', { from: range.from, to: range.to })
      .then(d => { if (alive) setReport(d); })
      .catch(e => { if (alive) setErr(e?.message || 'Could not load the report.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range.from, range.to]);

  const exportExcel = () => {
    setExportErr('');
    try { downloadBidReport(report); }
    catch (e) { setExportErr(e?.message || 'The spreadsheet could not be built.'); }
  };
  // The print stylesheet keys off this body class, so only the report
  // sections reach the page: no sidebar, header, tab strip or controls.
  const printSummary = () => {
    document.body.classList.add('printing-bid-report');
    const done = () => { document.body.classList.remove('printing-bid-report'); window.removeEventListener('afterprint', done); };
    window.addEventListener('afterprint', done);
    window.print();
    // Browsers that never fire afterprint (or a cancelled dialog) still get
    // the class cleared once the dialog has closed.
    setTimeout(done, 60000);
  };

  const sheets = report ? buildReportSheets(report) : [];
  const sections = sheets.filter(s => s.name !== 'Summary');
  const v = report?.values || {};
  const ceilings = v.framework_ceilings || { count: 0, total_gbp: 0 };
  const line = isDark ? 'rgb(51 65 85)' : '#E5E7EB';
  const card = { background: isDark ? 'rgb(30 41 59)' : '#fff', border: `1px solid ${line}`, borderRadius: 8, marginBottom: 14, overflowX: 'auto' };
  const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap', fontSize: 10, textTransform: 'uppercase', background: isDark ? 'rgb(15 23 42)' : '#F9FAFB', borderBottom: `1px solid ${line}` };
  const td = { padding: '7px 10px', color: isDark ? 'rgb(203 213 225)' : '#374151', borderBottom: `1px solid ${isDark ? 'rgb(51 65 85)' : '#F3F4F6'}`, verticalAlign: 'top' };
  const heading = (t, n) => (
    <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'baseline', gap: 8, borderBottom: `1px solid ${line}` }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827' }}>{t}</span>
      <span style={{ fontSize: 11, color: '#6B7280' }}>{n === 0 ? 'nothing in this period' : `${n} row${n === 1 ? '' : 's'}`}</span>
    </div>
  );
  // A cell from a sheet row: money columns in pounds, rates as a percentage
  // (the sheet holds "n/a" already where nothing was decided), the first
  // column a link to the bid where the row has one.
  const cell = (sheet, r, c, val) => {
    if (sheet.money.includes(c)) return val === '' ? <span style={{ color: '#9CA3AF' }}>—</span> : fmtMoney(val);
    if (sheet.percent.includes(c)) return typeof val === 'number' ? fmtPercent(val) : val;
    if (c === 0 && sheet.ids[r] != null) {
      return <span onClick={() => navigate('bid-detail', sheet.ids[r])} style={{ cursor: 'pointer', fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827' }}>{val === '' ? '—' : val}</span>;
    }
    if (sheet.headers[c] === 'Status now' || sheet.headers[c] === 'Status' || sheet.headers[c] === 'To' || (sheet.headers[c] === 'From' && val !== 'Created')) return <Pill text={val} palette={STATUS_COLORS} />;
    return val === '' ? <span style={{ color: '#9CA3AF' }}>—</span> : String(val);
  };

  return (
    <div className="bid-report">
      <div className="bid-report-controls" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }} data-tour="report-controls">
        <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280' }}>From</label>
        <input type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} style={{ ...fieldStyle(isDark), width: 'auto' }} />
        <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280' }}>To</label>
        <input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} style={{ ...fieldStyle(isDark), width: 'auto' }} />
        <button onClick={() => setRange(thisMonthRange())} style={btnSm(isDark)}>This month</button>
        <button onClick={() => setRange(lastMonthRange())} style={btnSm(isDark)}>Last month</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {exportErr && <span role="alert" style={{ fontSize: 11, fontWeight: 600, color: '#EF4444' }}>{exportErr}</span>}
          <button onClick={exportExcel} disabled={!report || loading} style={{ ...btnSm(isDark), opacity: !report || loading ? 0.5 : 1 }} title="One sheet per section plus a Summary">Export to Excel</button>
          <button onClick={printSummary} disabled={!report || loading} style={{ ...btnPrimary(isDark), padding: '4px 12px', fontSize: 11, opacity: !report || loading ? 0.5 : 1 }} title="Prints the report sections only">Print summary</button>
        </div>
      </div>

      <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827', marginBottom: 8 }}>
        Pipeline Tracker report{report ? `, ${fmtDateLong(report.from)} to ${fmtDateLong(report.to)}` : ''}
      </div>

      {err ? (
        <div style={{ padding: 16, fontSize: 12, color: '#EF4444' }}>{err}</div>
      ) : loading && !report ? (
        <div style={{ padding: 16, fontSize: 12, color: '#6B7280' }}>Loading…</div>
      ) : report ? (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {kpi(report.submitted?.length ?? 0, 'Submitted')}
            {kpi(report.won?.length ?? 0, 'Won', '#22C55E')}
            {kpi(report.lost?.length ?? 0, 'Lost', '#EF4444')}
            {kpi(report.no_bid?.length ?? 0, 'No-bid')}
            {kpi(report.in_flight?.length ?? 0, 'In flight', '#F97316')}
            {kpi(formatValueGbp(v.submitted_ex_vat ?? 0) || '£0', 'Submitted value ex VAT')}
            {kpi(`${ceilings.count ?? 0}${ceilings.total_gbp ? ' · ' + formatValueGbp(ceilings.total_gbp) : ''}`, 'Framework ceilings (counted, not added)')}
            {(v.unpriced ?? 0) > 0 && kpi(v.unpriced, 'Unpriced bids', '#B45309')}
          </div>

          <RootCauses bids={bids} card={card} heading={heading} />
          {sections.map(sheet => (
            <section key={sheet.name} style={card}>
              {heading(sheet.title, sheet.rows.length)}
              {sheet.rows.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead><tr>{sheet.headers.map((h, c) => <th key={h} style={{ ...th, textAlign: sheet.money.includes(c) || sheet.percent.includes(c) ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {sheet.rows.map((row, r) => (
                      <tr key={r}>
                        {row.map((val, c) => (
                          <td key={c} style={{ ...td, textAlign: sheet.money.includes(c) || sheet.percent.includes(c) ? 'right' : 'left', whiteSpace: c === 1 && sheet.ids.length ? 'normal' : 'nowrap' }}>{cell(sheet, r, c, val)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Buyer feedback on the lost bids mapped to the five root causes: the
// scoreboard for improvement, which used to be the Win / Loss Log tab.
function RootCauses({ bids, card, heading }) {
  const isDark = useDarkMode();
  const decided = bids.filter(b => b.result && b.result !== 'Won');
  if (!decided.length) return null;
  const freq = CAUSES.map(c => ({ ...c, n: decided.filter(b => b[c.key]).length }));
  const max = Math.max(1, ...freq.map(f => f.n));
  return (
    <section style={card}>
      {heading('Loss root causes (all time)', decided.length)}
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {freq.map(f => (
          <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }} title={f.help}>
            <div style={{ width: 180, fontSize: 11, color: isDark ? 'rgb(203 213 225)' : '#374151', flexShrink: 0 }}>{f.short}</div>
            <div style={{ flex: 1, background: isDark ? 'rgb(51 65 85)' : '#F3F4F6', borderRadius: 4, height: 12, overflow: 'hidden' }}>
              <div style={{ width: `${(f.n / max) * 100}%`, height: '100%', background: NAVY, borderRadius: 4 }} />
            </div>
            <div style={{ width: 20, textAlign: 'right', fontSize: 12, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#111827' }}>{f.n}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============ Bid create/edit form ============
// Two-way toggle for a value qualifier (ex / inc VAT and so on). Selection is
// drawn the same way as the All / Live / Needs attention control.
function Pair({ value, onChange, options, isDark }) {
  return (
    <div style={{ display: 'inline-flex', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, overflow: 'hidden' }}>
      {options.map(([v, label]) => (
        <button key={v} type="button" onClick={() => onChange(v)} style={{ ...segmentedStyle(isDark, value === v), padding: '4px 9px', fontSize: 11 }}>{label}</button>
      ))}
    </div>
  );
}

const USED_FOR = [['notice', 'Notice'], ['submission', 'Submission'], ['both', 'Both']];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
// The summary's limit, as src/api/bidStreamline.js enforces it.
export const SUMMARY_MAX = 300;
const emptyPlatform = () => ({ platform: '', used_for: 'submission', account_holder: '', buyer_reference: '', url: '', custom: false });

export function BidForm({ existing, kind = 'bid', columns, people = [], clients = [], onClose, onSaved }) {
  const isDark = useDarkMode();
  // A lead (migration 0417) is the same record with less to it: no deadline,
  // portals, submission types or key date, a source, the shorter status set,
  // and a next action with a due date in place of the deadline.
  const lead = (existing?.kind || kind) === 'lead';
  const noun = lead ? 'lead' : 'bid';
  // One job per field (migration 0407): the free-text value, notes and the
  // outcome note are gone from the form; the key date is a date with a label.
  const base = existing || { opportunity: '', buyer: '', client_id: '', sector: '', service_line: '', status: 'Upcoming', owner: '', owner_id: '', key_date: '', key_date_label: '', deadline: '', deadline_time: '', watch_date: '', source: '', summary: '', next_action: '', next_action_due: '', next_action_owner_id: '', value_gbp: null };
  const [f, setF] = useState(() => ({
    ...base,
    deadline_time: base.deadline_time || '',
    key_date: ISO_DATE.test(String(base.key_date || '')) ? base.key_date : '',
    value_vat: base.value_vat || 'ex', value_basis: base.value_basis || 'contract', value_confidence: base.value_confidence || 'estimated',
  }));
  const [cv, setCv] = useState(parseCustom(existing));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});     // field -> message, shown at that field
  const [serverErr, setServerErr] = useState(''); // what the API refused, shown next to Save
  const set = (k, v) => { setF(p => ({ ...p, [k]: v })); if (errors[k]) setErrors(p => ({ ...p, [k]: '' })); };
  const pMap = new Map((people || []).map(p => [p.id, `${p.first_name || ''} ${p.last_name || ''}`.trim()]));
  // Selecting a person sets both owner_id and the display name; clearing it
  // leaves any legacy free-text owner untouched.
  const setOwner = (id) => { setF(p => ({ ...p, owner_id: id, owner: id ? (pMap.get(id) || p.owner) : p.owner })); setErrors(p => ({ ...p, owner_id: '' })); };

  // Submission types (PQQ, ROM, ITT...) — a bid can carry more than one, so
  // this is a tag set stored separately (bid_submission_types), not a field
  // on the bid row. Only editable once the bid exists (needs an id).
  const [typeOpts, setTypeOpts] = useState([]);
  const [subTypes, setSubTypes] = useState(() => parseSubmissionTypes(existing?.submission_types));
  // Platforms: where the notice was seen and where the response goes in.
  // Picked from config-options bid_platform; "Other" reveals a free-text box.
  const [platformOpts, setPlatformOpts] = useState([]);
  const [serviceLines, setServiceLines] = useState([]);
  const [sources, setSources] = useState([]);
  const [platforms, setPlatforms] = useState(() => (Array.isArray(existing?.platforms) ? existing.platforms : []).map(p => ({ ...emptyPlatform(), ...p })));
  useEffect(() => {
    api.get('config-options', { category: 'bid_submission_type' })
      .then(d => setTypeOpts(Array.isArray(d) ? d.filter(o => o.is_active !== 0) : [])).catch(() => {});
    api.get('config-options', { category: 'bid_platform' })
      .then(d => setPlatformOpts(Array.isArray(d) ? d.filter(o => o.is_active !== 0).map(o => o.value) : [])).catch(() => {});
    api.get('config-options', { category: 'bid_service_line' })
      .then(d => setServiceLines(Array.isArray(d) ? d.filter(o => o.is_active !== 0) : [])).catch(() => {});
    if (lead) api.get('config-options', { category: 'bid_lead_source' })
      .then(d => setSources(Array.isArray(d) ? d.filter(o => o.is_active !== 0) : [])).catch(() => {});
  }, [lead]);
  // Choosing a client fills the buyer text from its name, so the two never
  // disagree; clearing it leaves whatever buyer text was typed.
  // The picker's list, plus any client created from the form itself so the
  // new one is selectable straight away.
  const [clientList, setClientList] = useState(clients);
  useEffect(() => { setClientList(clients); }, [clients]);
  const [newClient, setNewClient] = useState(null);   // null = closed, else { name, code, err, saving }
  const setClient = (id) => setF(p => ({ ...p, client_id: id, buyer: id ? (clientList.find(c => c.id === id)?.name || p.buyer) : p.buyer }));
  const chooseClient = (v) => { if (v === '__new__') setNewClient({ name: f.buyer || '', code: '', err: '', saving: false }); else setClient(v); };
  const createClient = async () => {
    const name = (newClient?.name || '').trim();
    if (!name) { setNewClient(n => ({ ...n, err: 'The client needs a name.' })); return; }
    const dup = clientList.find(c => c.name.trim().toLowerCase() === name.toLowerCase());
    if (dup) { setNewClient(null); setClient(dup.id); return; }
    setNewClient(n => ({ ...n, saving: true, err: '' }));
    try {
      const created = await api.post('clients', { name, code: (newClient.code || '').trim() || null });
      const row = { id: created.id, name, code: (newClient.code || '').trim() || null };
      setClientList(list => [...list, row].sort((x, y) => x.name.localeCompare(y.name)));
      setNewClient(null);
      // setClient reads clientList, which has not re-rendered yet, so the name
      // is set alongside the id here.
      setF(p => ({ ...p, client_id: created.id, buyer: name }));
      setClient(created.id);
    } catch (e) {
      setNewClient(n => ({ ...n, saving: false, err: e?.message || 'Could not create the client.' }));
    }
  };
  // A stored platform that is not in the picklist is shown as Other with its
  // text, once the picklist has arrived.
  useEffect(() => {
    if (!platformOpts.length) return;
    setPlatforms(rows => rows.map(r => (r.platform && !platformOpts.includes(r.platform) ? { ...r, custom: true } : r)));
  }, [platformOpts]);
  const toggleType = (v) => setSubTypes(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]);
  const platformChoices = platformOpts.includes('Other') ? platformOpts : [...platformOpts, 'Other'];
  const setPlatform = (i, patch) => setPlatforms(rows => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const choosePlatform = (i, v) => setPlatform(i, v === 'Other' ? { platform: '', custom: true } : { platform: v, custom: false });

  const save = async () => {
    const errs = {};
    if (!f.opportunity.trim()) errs.opportunity = lead ? 'What the lead is for is required' : 'Opportunity name is required';
    if (people.length > 0 && !f.owner_id) errs.owner_id = 'Owner is required';
    if (lead) {
      // A lead's clock is its follow-up: without one it goes quiet. A decided
      // lead needs none.
      if (!OUTCOME_STATUSES.includes(f.status) && (!String(f.next_action || '').trim() || !f.next_action_due)) errs.next_action = 'A lead needs a next action and the date it is due';
    } else if (!f.deadline && !f.watch_date) {
      // A bid on a pipeline notice has no deadline yet, only a date to look again.
      errs.deadline = 'A deadline or a watch date is required';
    }
    setErrors(errs); setServerErr('');
    if (Object.keys(errs).length) return;
    setSaving(true);
    const body = {
      opportunity: f.opportunity.trim(), buyer: f.buyer, client_id: f.client_id || null, sector: f.sector, service_line: f.service_line || null,
      status: f.status, owner: f.owner, owner_id: f.owner_id || null,
      deadline: f.deadline || null, deadline_time: f.deadline_time || null, watch_date: f.watch_date || null, summary: f.summary,
      key_date: f.key_date || null, key_date_label: f.key_date ? (f.key_date_label || null) : null,
      next_action: f.next_action || null, next_action_due: f.next_action_due || null, next_action_owner_id: f.next_action_owner_id || null,
      value_gbp: f.value_gbp === '' || f.value_gbp == null ? null : Number(f.value_gbp),
      value_vat: f.value_vat, value_basis: f.value_basis, value_confidence: f.value_confidence,
      platforms: platforms
        .filter(p => (p.platform || '').trim() || p.custom)
        .map(p => ({ platform: (p.platform || '').trim() || 'Other', used_for: p.used_for || 'submission', account_holder: p.account_holder || null, buyer_reference: p.buyer_reference || null, url: p.url || null })),
    };
    if (lead) {
      // Nothing a lead does not carry is sent, so editing one never clears a
      // tender's fields by accident.
      for (const k of ['deadline', 'deadline_time', 'watch_date', 'key_date', 'key_date_label', 'platforms']) delete body[k];
      body.kind = 'lead';
      body.source = f.source || null;
    }
    // Stamp previous_status whenever the workflow stage actually changes, so
    // it can be stepped back one move (same convention as SOW statuses).
    if (existing && f.status !== base.status) body.previous_status = base.status;
    if (columns.length) {
      const custom = {};
      for (const col of columns) { const v = cv[col.key]; if (v != null && v !== '') custom[col.key] = v; }
      body.custom_json = JSON.stringify(custom);
    }
    try {
      let bidId = existing?.id;
      let saved = null;
      if (existing) saved = await api.put('crm/bids/' + existing.id, body);
      else { saved = await api.post('crm/bids', body); bidId = saved.id; }
      if (bidId && !lead) await api.put(`crm/bids/${bidId}/submission-types`, { types: subTypes }).catch(() => {});
      onSaved(saved);
    } catch (e) {
      // The API's own words (an outcome rule, a missing owner) belong next to
      // the button that was pressed, not in a toast at the corner of the page.
      setServerErr(e?.message || `Could not save this ${noun}.`);
    } finally { setSaving(false); }
  };

  const fieldErr = (k) => errors[k] ? <div style={{ fontSize: 10, color: '#EF4444', fontWeight: 600, marginTop: 3 }}>{errors[k]}</div> : null;
  const errBorder = (k) => errors[k] ? { border: '1px solid #EF4444' } : null;
  const hint = { fontSize: 10, color: '#9CA3AF', marginTop: 3 };

  return (
    <Modal title={existing ? `Edit ${noun}` : `New ${noun}`} onClose={onClose}>
      {lead && !existing && <div style={{ ...hint, marginTop: 0, marginBottom: 4 }}>A conversation that is not a formal tender: a referral, an enquiry, an MSP renewal. It sits on the Leads tab and stays out of the tender pipeline figures. If a formal ITT arrives, convert it to a tender from the lead's More menu.</div>}
      <label style={lbl(isDark)}>{lead ? 'Lead *' : 'Opportunity *'}</label>
      <input value={f.opportunity} onChange={e => set('opportunity', e.target.value)} placeholder={lead ? 'e.g. Shawpak managed service' : undefined} style={{ ...fieldStyle(isDark), ...errBorder('opportunity') }} />
      {fieldErr('opportunity')}
      <label style={lbl(isDark)}>Client</label>
      <select value={f.client_id || ''} onChange={e => chooseClient(e.target.value)} style={fieldStyle(isDark)}>
        <option value="">Not linked to a client yet</option>
        {clientList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        <option value="__new__">+ New client…</option>
      </select>
      {newClient && (
        <div style={{ border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, padding: 8, marginTop: 6 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <input value={newClient.name} onChange={e => setNewClient(n => ({ ...n, name: e.target.value, err: '' }))} placeholder="Client name, as on the notice" autoFocus style={{ ...fieldStyle(isDark), flex: 2, minWidth: 160 }} />
            <input value={newClient.code} onChange={e => setNewClient(n => ({ ...n, code: e.target.value }))} placeholder="Short code (optional)" style={{ ...fieldStyle(isDark), flex: 1, minWidth: 100 }} />
            <button type="button" onClick={createClient} disabled={newClient.saving} style={btnPrimary(isDark)}>{newClient.saving ? 'Adding…' : 'Add client'}</button>
            <button type="button" onClick={() => setNewClient(null)} style={btnSm(isDark)}>Cancel</button>
          </div>
          <div style={hint}>Creates the client in Clients as well, so SOWs and contacts can use it. If the name already exists it is picked instead of duplicated.</div>
          {newClient.err && <div role="alert" style={{ fontSize: 10, color: '#EF4444', fontWeight: 600, marginTop: 3 }}>{newClient.err}</div>}
        </div>
      )}
      {!f.client_id && (
        <>
          <label style={lbl(isDark)}>{lead ? 'Organisation' : 'Buyer (as named on the notice)'}</label>
          <input value={f.buyer || ''} onChange={e => set('buyer', e.target.value)} placeholder="Until the client exists in Clients" style={fieldStyle(isDark)} />
        </>
      )}
      {lead && (
        <>
          <label style={lbl(isDark)}>Source</label>
          <select value={f.source || ''} onChange={e => set('source', e.target.value)} style={fieldStyle(isDark)}>
            <option value="">—</option>
            {sources.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            {f.source && !sources.some(o => o.value === f.source) && <option value={f.source}>{f.source}</option>}
          </select>
        </>
      )}
      <label style={lbl(isDark)}>Sector</label>
      <input value={f.sector || ''} onChange={e => set('sector', e.target.value)} placeholder="e.g. Local Gov, Defence" style={fieldStyle(isDark)} />
      <label style={lbl(isDark)}>Service line</label>
      <select value={f.service_line || ''} onChange={e => set('service_line', e.target.value)} style={fieldStyle(isDark)}>
        <option value="">—</option>
        {serviceLines.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        {f.service_line && !serviceLines.some(o => o.value === f.service_line) && <option value={f.service_line}>{f.service_line} (not in the picklist)</option>}
      </select>
      {existing?.service_line_legacy && <div style={hint}>Was “{existing.service_line_legacy}” before the picklist.</div>}

      <label style={lbl(isDark)}>Value (£)</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="number" min="0" step="1" inputMode="numeric" value={f.value_gbp ?? ''} onChange={e => set('value_gbp', e.target.value === '' ? null : e.target.value)}
          placeholder="e.g. 220000" style={{ ...fieldStyle(isDark), width: 140 }} />
        <Pair isDark={isDark} value={f.value_vat} onChange={v => set('value_vat', v)} options={[['ex', 'ex VAT'], ['inc', 'inc VAT']]} />
        <Pair isDark={isDark} value={f.value_basis} onChange={v => set('value_basis', v)} options={[['contract', 'Contract value'], ['framework_ceiling', 'Framework ceiling']]} />
        <Pair isDark={isDark} value={f.value_confidence} onChange={v => set('value_confidence', v)} options={[['estimated', 'Estimated'], ['verified', 'Verified']]} />
      </div>
      {f.value_gbp != null && f.value_gbp !== '' && <div style={hint}>{formatValueGbp(Number(f.value_gbp))}{f.value_vat === 'inc' ? ` inc VAT (${formatValueGbp(Number(f.value_gbp) / 1.2)} ex VAT)` : ' ex VAT'}{f.value_basis === 'framework_ceiling' ? '. A framework ceiling is shown but kept out of pipeline totals.' : ''}</div>}

      <label style={lbl(isDark)}>Status</label>
      <select value={f.status} onChange={e => set('status', e.target.value)} style={fieldStyle(isDark)}>
        {(lead ? LEAD_STATUSES : STATUSES).map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      {existing && <div style={hint}>For a guided step forward/back, use the workflow stepper on the {noun}'s detail page instead.</div>}
      {!lead && <>
      <label style={lbl(isDark)}>Submission type(s)</label>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {typeOpts.map(o => {
          const on = subTypes.includes(o.value);
          return (
            <button key={o.value} type="button" onClick={() => toggleType(o.value)}
              style={{ padding: '3px 9px', fontSize: 11, fontWeight: 700, borderRadius: 10, cursor: 'pointer', fontFamily: 'Montserrat,sans-serif',
                border: `1px solid ${on ? NAVY : '#E5E7EB'}`, background: on ? NAVY : '#fff', color: on ? '#fff' : '#374151' }}
              title={o.label}>
              {o.value}
            </button>
          );
        })}
      </div>
      </>}

      <label style={lbl(isDark)}>Owner *</label>
      {people.length > 0 ? (
        <>
          <select value={f.owner_id || ''} onChange={e => setOwner(e.target.value)} style={{ ...fieldStyle(isDark), ...errBorder('owner_id') }}>
            <option value="">Choose an owner</option>
            {people.map(p => <option key={p.id} value={p.id}>{`${p.first_name || ''} ${p.last_name || ''}`.trim()}</option>)}
          </select>
          {fieldErr('owner_id')}
          {!f.owner_id && f.owner ? <div style={hint}>Currently recorded as “{f.owner}”. Pick a person above to assign it.</div> : null}
        </>
      ) : (
        <input value={f.owner || ''} onChange={e => set('owner', e.target.value)} style={fieldStyle(isDark)} />
      )}

      {!lead && <>
      <label style={lbl(isDark)}>Deadline (submission / closing) *</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="date" value={f.deadline || ''} onChange={e => set('deadline', e.target.value)} style={{ ...fieldStyle(isDark), width: 'auto', ...errBorder('deadline') }} />
        <input type="time" value={f.deadline_time || ''} onChange={e => set('deadline_time', e.target.value)} title="Closing time, UK clock" style={{ ...fieldStyle(isDark), width: 'auto' }} />
        <span style={{ fontSize: 11, color: isDark ? 'rgb(148 163 184)' : '#6B7280' }}>or a watch date</span>
        <input type="date" value={f.watch_date || ''} onChange={e => { set('watch_date', e.target.value); if (errors.deadline) setErrors(p => ({ ...p, deadline: '' })); }} title="No deadline yet: the date to look at this notice again" aria-label="Watch date" style={{ ...fieldStyle(isDark), width: 'auto' }} />
      </div>
      {fieldErr('deadline')}
      <label style={lbl(isDark)}>Key date</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="date" value={f.key_date || ''} onChange={e => set('key_date', e.target.value)} aria-label="Key date" style={{ ...fieldStyle(isDark), width: 'auto' }} />
        <input value={f.key_date_label || ''} onChange={e => set('key_date_label', e.target.value)} maxLength={60} placeholder="What it is, e.g. Clarification deadline" aria-label="What the key date is" style={{ ...fieldStyle(isDark), flex: '1 1 200px', width: 'auto' }} />
      </div>
      <div style={hint}>The next date that matters other than the deadline: clarifications closing, a site visit, a presentation.</div>
      </>}
      <label style={lbl(isDark)}>{lead ? 'Next action *' : 'Next action'}</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input value={f.next_action || ''} onChange={e => set('next_action', e.target.value)} maxLength={200} placeholder={lead ? 'The follow-up, e.g. Call to arrange a site visit' : 'The one next step on this bid'} style={{ ...fieldStyle(isDark), flex: '1 1 240px', width: 'auto', ...errBorder('next_action') }} />
        <input type="date" value={f.next_action_due || ''} onChange={e => { set('next_action_due', e.target.value); if (errors.next_action) setErrors(p => ({ ...p, next_action: '' })); }} title="By when" aria-label="Next action due" style={{ ...fieldStyle(isDark), width: 'auto', ...errBorder('next_action') }} />
        {people.length > 0 && (
          <select value={f.next_action_owner_id || ''} onChange={e => set('next_action_owner_id', e.target.value)} aria-label="Who the next action is on" style={{ ...fieldStyle(isDark), width: 'auto' }}>
            <option value="">On the owner</option>
            {people.map(p => <option key={p.id} value={p.id}>{`On ${p.first_name || ''} ${p.last_name || ''}`.trim()}</option>)}
          </select>
        )}
      </div>

      {fieldErr('next_action')}

      {!lead && <>
      <label style={lbl(isDark)}>Portals</label>
      {platforms.length === 0 && <div style={hint}>Where the notice was published and where the response is submitted (ProContract, Jaggaer, In-tend...), with the account that holds the login and the buyer's reference.</div>}
      {platforms.map((p, i) => (
        <div key={i} style={{ border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, padding: 8, marginTop: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 6 }}>
          <div>
            <select value={p.custom ? 'Other' : (p.platform || '')} onChange={e => choosePlatform(i, e.target.value)} style={fieldStyle(isDark)}>
              <option value="">Platform</option>
              {platformChoices.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            {p.custom && <input value={p.platform || ''} onChange={e => setPlatform(i, { platform: e.target.value })} placeholder="Name the platform" style={{ ...fieldStyle(isDark), marginTop: 4 }} />}
          </div>
          <select value={p.used_for || 'submission'} onChange={e => setPlatform(i, { used_for: e.target.value })} style={fieldStyle(isDark)} title="Used for">
            {USED_FOR.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input value={p.account_holder || ''} onChange={e => setPlatform(i, { account_holder: e.target.value })} placeholder="Account holder" style={fieldStyle(isDark)} />
          <input value={p.buyer_reference || ''} onChange={e => setPlatform(i, { buyer_reference: e.target.value })} placeholder="Buyer reference" style={fieldStyle(isDark)} />
          <div style={{ display: 'flex', gap: 4 }}>
            <input value={p.url || ''} onChange={e => setPlatform(i, { url: e.target.value })} placeholder="Link" style={fieldStyle(isDark)} />
            <button type="button" onClick={() => setPlatforms(rows => rows.filter((_, j) => j !== i))} title="Remove this platform" style={{ ...btnSm(isDark), padding: '2px 7px', color: '#EF4444' }}>✕</button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => setPlatforms(rows => [...rows, emptyPlatform()])} style={{ ...btnSm(isDark), marginTop: 6 }}>+ Add platform</button>
      </>}

      <label style={lbl(isDark)}>Summary</label>
      <textarea value={f.summary || ''} onChange={e => set('summary', e.target.value)} rows={3} maxLength={SUMMARY_MAX} placeholder="The standing picture in a line or two: what it is, the incumbent, how it is evaluated. Dated events go on the bid's timeline." style={{ ...fieldStyle(isDark), resize: 'vertical' }} />
      <div style={{ ...hint, textAlign: 'right', ...((f.summary || '').length > SUMMARY_MAX ? { color: '#EF4444', fontWeight: 600 } : null) }}>{(f.summary || '').length} / {SUMMARY_MAX}{(f.summary || '').length > SUMMARY_MAX ? ': shorten it, and put the detail on the timeline' : ''}</div>

      {columns.map(col => (
        <div key={col.key}>
          <label style={lbl(isDark)}>{col.label}</label>
          {col.type === 'select' ? (
            <select value={cv[col.key] || ''} onChange={e => setCv(p => ({ ...p, [col.key]: e.target.value }))} style={fieldStyle(isDark)}>
              <option value=""></option>
              {parseOptions(col).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'}
              value={cv[col.key] || ''} onChange={e => setCv(p => ({ ...p, [col.key]: e.target.value }))} style={fieldStyle(isDark)} />
          )}
        </div>
      ))}

      <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
        {serverErr && <span role="alert" style={{ fontSize: 11, fontWeight: 600, color: '#EF4444', marginRight: 'auto' }}>{serverErr}</span>}
        <button onClick={onClose} style={btnSm(isDark)}>Cancel</button>
        <button onClick={save} disabled={saving} style={btnPrimary(isDark)}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

// ============ Record-outcome form ============
// `target` is the status the person is moving the bid into: 'Won', 'Lost' or
// 'No-bid' from the detail page's workflow buttons, or undefined from a plain
// "Record outcome". The API refuses an outcome without a result date, and a
// no-bid without a reason, so the form asks for those before it sends.
const LOST_RESULTS = ['Lost', 'Deselected', 'Excluded'];

// The toast after an outcome from the board or the list: a win says where its
// hardware went, or why it is still waiting on the bid.
function outcomeToast(showToast, res) {
  const c = res?.hardware_carry;
  if (c?.error) showToast(`Outcome saved. ${c.error}`, 'error');
  else if (c?.carried) showToast(`Outcome saved. ${c.carried} hardware line${c.carried === 1 ? '' : 's'} ${c.created ? 'raised' : 'added to'} ${c.sow?.reference} on the Opportunity Tracker.`);
  else showToast('Outcome saved');
}

export function OutcomeForm({ b, target, onClose, onSaved }) {
  const isDark = useDarkMode();
  const isNoBid = target === 'No-bid';
  const [result, setResult] = useState(
    target === 'Won' ? 'Won'
    : target === 'Lost' ? (LOST_RESULTS.includes(b.result) ? b.result : 'Lost')
    : (b.result || ''));
  // result_date used to be free text ("10-Sep-25"). Anything that is not an
  // ISO date is shown for reference and has to be picked again.
  const legacyDate = b.result_date && !ISO_DATE.test(b.result_date) ? b.result_date : null;
  const [date, setDate] = useState(legacyDate ? '' : (b.result_date || ''));
  const [score, setScore] = useState(b.result_score || '');
  const [feedback, setFeedback] = useState(b.feedback || '');
  const [supplier, setSupplier] = useState(b.winning_supplier || '');
  const [price, setPrice] = useState(b.winning_price_gbp ?? '');
  const [reason, setReason] = useState(b.no_bid_reason || '');
  const [reasonOpts, setReasonOpts] = useState([]);
  const [causes, setCauses] = useState(Object.fromEntries(CAUSES.map(c => [c.key, !!b[c.key]])));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState('');

  useEffect(() => {
    if (!isNoBid) return;
    api.get('config-options', { category: 'bid_no_bid_reason' })
      .then(d => setReasonOpts(Array.isArray(d) ? d.filter(o => o.is_active !== 0) : [])).catch(() => {});
  }, [isNoBid]);

  const targetStatus = isNoBid ? 'No-bid' : result === 'Won' ? 'Won' : LOST_RESULTS.includes(result) ? 'Lost' : null;

  // Recording a win carries the bid's hardware onto the Opportunity Tracker,
  // so the form says so before Save rather than after.
  const [hardwareToCarry, setHardwareToCarry] = useState(0);
  useEffect(() => {
    if (isNoBid) return;
    api.get(`crm/bids/${b.id}/hardware`).then(d => setHardwareToCarry(d?.to_carry || 0)).catch(() => {});
  }, [b.id, isNoBid]);

  const save = async () => {
    const errs = {};
    if (isNoBid && !reason) errs.reason = 'A no-bid reason is required';
    if (targetStatus && !date) errs.date = 'A result date is required';
    setErrors(errs); setServerErr('');
    if (Object.keys(errs).length) return;
    setSaving(true);
    const body = {
      result_date: date || null, result_score: score, feedback,
      winning_supplier: supplier || null,
      winning_price_gbp: price === '' || price == null ? null : Number(price),
    };
    for (const c of CAUSES) body[c.key] = causes[c.key] ? 1 : 0;
    if (isNoBid) {
      body.status = 'No-bid';
      body.no_bid_reason = reason;
    } else {
      body.result = result || null;
      if (targetStatus) body.status = targetStatus;
    }
    // Stamp previous_status so the bid can be reopened back to whichever
    // stage it was actually in, not just a default fallback (same convention
    // as the workflow stepper's quick Park action).
    if (body.status && body.status !== b.status) body.previous_status = b.status;
    try { onSaved(await api.put('crm/bids/' + b.id, body)); }
    catch (e) { setServerErr(e?.message || 'Could not save this outcome.'); }
    finally { setSaving(false); }
  };

  const fieldErr = (k) => errors[k] ? <div style={{ fontSize: 10, color: '#EF4444', fontWeight: 600, marginTop: 3 }}>{errors[k]}</div> : null;
  const errBorder = (k) => errors[k] ? { border: '1px solid #EF4444' } : null;
  const title = isNoBid ? `Record no-bid: ${b.opportunity}`
    : target === 'Won' ? `Record a win: ${b.opportunity}`
    : target === 'Lost' ? `Record a loss: ${b.opportunity}`
    : `Record outcome: ${b.opportunity}`;

  return (
    <Modal title={title} onClose={onClose}>
      {isNoBid ? (
        <>
          <label style={lbl(isDark)}>No-bid reason *</label>
          <select value={reason} onChange={e => { setReason(e.target.value); setErrors(p => ({ ...p, reason: '' })); }} style={{ ...fieldStyle(isDark), ...errBorder('reason') }}>
            <option value="">Choose a reason</option>
            {reasonOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            {reason && !reasonOpts.some(o => o.value === reason) && <option value={reason}>{reason}</option>}
          </select>
          {fieldErr('reason')}
        </>
      ) : target === 'Won' ? (
        <>
          <label style={lbl(isDark)}>Result</label>
          <div><Pill text="Won" palette={RESULT_COLORS} /></div>
        </>
      ) : (
        <>
          <label style={lbl(isDark)}>Result</label>
          <select value={result} onChange={e => setResult(e.target.value)} style={fieldStyle(isDark)}>
            {target !== 'Lost' && <option value="">(undecided)</option>}
            {(target === 'Lost' ? LOST_RESULTS : RESULTS).map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </>
      )}
      <label style={lbl(isDark)}>Result date{targetStatus ? ' *' : ''}</label>
      <input type="date" value={date} onChange={e => { setDate(e.target.value); setErrors(p => ({ ...p, date: '' })); }} style={{ ...fieldStyle(isDark), width: 'auto', ...errBorder('date') }} />
      {fieldErr('date')}
      {legacyDate && <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 3 }}>Previously recorded as “{legacyDate}”. Pick the date to keep it.</div>}
      {!isNoBid && (
        <>
          <label style={lbl(isDark)}>Score</label>
          <input value={score} onChange={e => setScore(e.target.value)} placeholder="e.g. 52.5%, 0–2, Narrow, —" style={fieldStyle(isDark)} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: 160 }}>
              <label style={lbl(isDark)}>Winning supplier</label>
              <input value={supplier} onChange={e => setSupplier(e.target.value)} placeholder={result === 'Won' ? 'Alton Valley' : 'Who won it, if known'} style={fieldStyle(isDark)} />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={lbl(isDark)}>Winning price (£)</label>
              <input type="number" min="0" step="1" inputMode="numeric" value={price} onChange={e => setPrice(e.target.value)} placeholder="e.g. 210000" style={fieldStyle(isDark)} />
            </div>
          </div>
        </>
      )}
      {!isNoBid && result !== 'Won' && (
        <>
          <label style={lbl(isDark)}>Root causes (tick where the feedback cited them)</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            {CAUSES.map(c => (
              <label key={c.key} style={{ fontSize: 11, display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="checkbox" checked={causes[c.key]} onChange={e => setCauses(p => ({ ...p, [c.key]: e.target.checked }))} />
                <span><b>{c.short}</b> <span style={{ color: '#9CA3AF' }}>— {c.help}</span></span>
              </label>
            ))}
          </div>
        </>
      )}
      {result === 'Won' && b.status !== 'Won' && hardwareToCarry > 0 && (
        <div role="note" style={{ marginTop: 10, fontSize: 11, borderRadius: 4, padding: '6px 9px', background: isDark ? 'rgba(59,130,246,0.12)' : '#EFF6FF', color: isDark ? '#93C5FD' : '#1D4ED8' }}>
          {hardwareToCarry} hardware line{hardwareToCarry === 1 ? '' : 's'} from this bid's quotes and deals will go onto the Opportunity Tracker as a Draft opportunity.
        </div>
      )}
      <label style={lbl(isDark)}>{isNoBid ? 'Notes' : 'Buyer feedback (verbatim highlight)'}</label>
      <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={3} style={{ ...fieldStyle(isDark), resize: 'vertical' }} />
      <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
        {serverErr && <span role="alert" style={{ fontSize: 11, fontWeight: 600, color: '#EF4444', marginRight: 'auto' }}>{serverErr}</span>}
        <button onClick={onClose} style={btnSm(isDark)}>Cancel</button>
        <button onClick={save} disabled={saving} style={btnPrimary(isDark)}>{saving ? 'Saving…' : isNoBid ? 'Record no-bid' : 'Save outcome'}</button>
      </div>
    </Modal>
  );
}

// ============ Fields manager ============
// Option A of the CRM redesign (29 September 2026): the list became rows, so
// "⚙ Columns" became Fields. A person picks the fields shown as chips under
// each row, and their order, saved to their login. Adding a field for
// everyone is the same custom column as before (shared with the team), and an
// admin can delete one.
function FieldsManager({ fields, columns, isAdmin, updateView, reloadColumns, onClose, showToast }) {
  const isDark = useDarkMode();
  const [editing, setEditing] = useState(undefined); // undefined closed, null new, obj edit
  const all = [
    ...ROW_FIELDS.map(x => ({ key: x.key, label: x.label })),
    ...columns.map(c => ({ key: 'custom:' + c.key, label: c.label, def: c })),
  ];
  const byKey = new Map(all.map(x => [x.key, x]));
  const setFields = (next) => updateView({ fields: next });
  const removeCol = async (def) => {
    if (!confirm(`Delete the field "${def.label}" for everyone? Saved values for it will no longer be shown.`)) return;
    try { await api.delete('crm/columns/' + def.id); await reloadColumns(); setFields(fields.filter(k => k !== 'custom:' + def.key)); showToast('Field deleted'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  if (editing !== undefined) {
    return <ColumnForm def={editing} onClose={() => setEditing(undefined)}
      onSaved={async () => { setEditing(undefined); await reloadColumns(); showToast('Field saved'); }}
      onError={m => showToast(m, 'error')} />;
  }

  const rowStyle = { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: `1px solid ${isDark ? 'rgb(51 65 85)' : '#F3F4F6'}` };
  const tag = (t) => <span style={{ fontSize: 10, color: '#6B7280', background: isDark ? 'rgb(15 23 42)' : '#F3F4F6', padding: '1px 6px', borderRadius: 8 }}>{t}</span>;
  const customActions = (x) => x.def && (
    <>
      {tag('for everyone')}
      <button onClick={() => setEditing(x.def)} style={{ ...btnSm(isDark), padding: '2px 8px' }}>Edit</button>
      {isAdmin && <button onClick={() => removeCol(x.def)} style={{ ...btnSm(isDark), padding: '2px 8px', color: '#EF4444', borderColor: '#FCA5A5' }}>Delete</button>}
    </>
  );
  const shown = fields.filter(k => byKey.has(k));

  return (
    <Modal title="Fields" onClose={onClose} maxWidth={480}>
      <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>
        Pick the fields shown under each bid, and their order. Your choice is saved to your login and follows you between machines. A field added for everyone, and its values, is shared with the team.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {shown.map((k, i) => (
          <div key={k} style={rowStyle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked onChange={() => setFields(fields.filter(x => x !== k))} />
              {byKey.get(k).label || '(unnamed)'}
            </label>
            {customActions(byKey.get(k))}
            <button onClick={() => setFields(moveField(fields, k, -1))} disabled={i === 0} aria-label="Move up" style={{ ...btnSm(isDark), padding: '2px 6px', opacity: i === 0 ? 0.4 : 1 }}>▲</button>
            <button onClick={() => setFields(moveField(fields, k, 1))} disabled={i === shown.length - 1} aria-label="Move down" style={{ ...btnSm(isDark), padding: '2px 6px', opacity: i === shown.length - 1 ? 0.4 : 1 }}>▼</button>
          </div>
        ))}
        {all.filter(x => !fields.includes(x.key)).map(x => (
          <div key={x.key} style={rowStyle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={false} onChange={() => setFields([...fields, x.key])} />
              {x.label || '(unnamed)'}
            </label>
            {customActions(x)}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button onClick={() => setFields([])} style={btnSm(isDark)}>Clear</button>
        <button onClick={() => setEditing(null)} style={btnPrimary(isDark)}>+ Add a field for everyone</button>
        <button onClick={onClose} style={btnSm(isDark)}>Done</button>
      </div>
    </Modal>
  );
}

function ColumnForm({ def, onClose, onSaved, onError }) {
  const isDark = useDarkMode();
  const [label, setLabel] = useState(def ? def.label : '');
  const [type, setType] = useState(def ? def.type : 'text');
  const [options, setOptions] = useState(def ? parseOptions(def).join('\n') : '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!label.trim()) { onError('Column name is required'); return; }
    setSaving(true);
    const body = { label: label.trim(), type };
    if (type === 'select') body.options = options.split('\n').map(s => s.trim()).filter(Boolean);
    try {
      if (def) await api.put('crm/columns/' + def.id, body);
      else await api.post('crm/columns', body);
      onSaved();
    } catch (e) { onError(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal title={def ? 'Edit column' : 'Add column'} onClose={onClose} maxWidth={420}>
      <label style={lbl(isDark)}>Column name</label>
      <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Contract length" style={fieldStyle(isDark)} />
      <label style={lbl(isDark)}>Type</label>
      <select value={type} onChange={e => setType(e.target.value)} style={fieldStyle(isDark)}>
        {COL_TYPES.map(t => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
      </select>
      {type === 'select' && (
        <>
          <label style={lbl(isDark)}>Options (one per line)</label>
          <textarea value={options} onChange={e => setOptions(e.target.value)} rows={4} placeholder={'1 year\n3 years\n5 years'} style={{ ...fieldStyle(isDark), resize: 'vertical' }} />
        </>
      )}
      <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={btnSm(isDark)}>Cancel</button>
        <button onClick={save} disabled={saving} style={btnPrimary(isDark)}>{saving ? 'Saving…' : (def ? 'Save' : 'Add column')}</button>
      </div>
    </Modal>
  );
}
